'use client'
import {
  NetworkError,
  __async,
  __export,
  __spreadProps,
  __spreadValues,
  parseUnionAddress,
  toUnion
} from "../chunk-RCUVMCOA.mjs";

// src/client/solana/solana-sdk-factory.reatom.ts
import { atom } from "@reatom/core";
import { Connection } from "@solana/web3.js";
import { createContainer } from "ditox";

// src/client/solana/api/api-reatom.ts
import {
  CollectionControllerApi,
  ItemControllerApi,
  OrderControllerApi,
  Configuration as UnionConfiguration
} from "@rarible/api-client";
import crossFetch from "cross-fetch";
var safeFetch = (input, init) => crossFetch(input, init);
function createSolanaApi(params) {
  const { apiKey, unionApiPath } = params;
  const unionApiConfig = {
    basePath: unionApiPath,
    apiKey,
    fetchApi: safeFetch,
    headers: typeof apiKey === "string" ? { "X-API-KEY": apiKey } : {},
    exceptionHandler: (error, url, init) => __async(this, null, function* () {
      var _a;
      throw new NetworkError({
        status: -1,
        url: decodeURIComponent(url),
        formData: (_a = init == null ? void 0 : init.body) == null ? void 0 : _a.toString(),
        method: init == null ? void 0 : init.method,
        data: { message: error.message }
      });
    })
  };
  const unionConfiguration = new UnionConfiguration(unionApiConfig);
  return {
    union: {
      collection: new CollectionControllerApi(unionConfiguration),
      item: new ItemControllerApi(unionConfiguration),
      order: new OrderControllerApi(unionConfiguration)
    }
  };
}

// src/client/solana/solana-container-tokens.reatom.ts
var solana_container_tokens_reatom_exports = {};
__export(solana_container_tokens_reatom_exports, {
  API_ATOM: () => API_ATOM,
  CONNECTION_ATOM: () => CONNECTION_ATOM,
  MARKET_IDENTIFIER_ATOM: () => MARKET_IDENTIFIER_ATOM,
  MARKET_PROGRAM_ADDRESS_ATOM: () => MARKET_PROGRAM_ADDRESS_ATOM,
  PRIMARY_CURRENCY_ATOM: () => PRIMARY_CURRENCY_ATOM,
  RPC_URL_ATOM: () => RPC_URL_ATOM,
  SIGNER_ATOM: () => SIGNER_ATOM
});
import { token } from "ditox";
var SIGNER_ATOM = token("solanaSignerAtom");
var CONNECTION_ATOM = token("solanaConnectionAtom");
var API_ATOM = token("solanaApi");
var MARKET_PROGRAM_ADDRESS_ATOM = token(
  "solanaMarketProgramAddressAtom"
);
var MARKET_IDENTIFIER_ATOM = token(
  "solanaMarketIdentifierAtom"
);
var RPC_URL_ATOM = token("solanaRpcUrlAtom");
var PRIMARY_CURRENCY_ATOM = token(
  "solanaPrimaryCurrencyAtom"
);

// src/client/solana/solana-sdk-factory.reatom.ts
function createReatomSolanaContainer({
  signer,
  rpcUrl,
  unionApi,
  apiKey,
  marketProgramAddress,
  marketIdentifier,
  primaryCurrency,
  parentContainer
}) {
  const container = parentContainer ? createContainer(parentContainer) : createContainer();
  const api = createSolanaApi({ apiKey, unionApiPath: unionApi });
  const connection = new Connection(rpcUrl, "confirmed");
  const signerAtom = atom(signer, "solanaSigner");
  const connectionAtom = atom(connection, "solanaConnection");
  const marketProgramAddressAtom = atom(
    marketProgramAddress,
    "solanaMarketProgramAddress"
  );
  const marketIdentifierAtom = atom(marketIdentifier, "solanaMarketIdentifier");
  const rpcUrlAtom = atom(rpcUrl, "solanaRpcUrl");
  const primaryCurrencyAtom = atom(primaryCurrency, "solanaPrimaryCurrency");
  container.bindValue(SIGNER_ATOM, signerAtom);
  container.bindValue(CONNECTION_ATOM, connectionAtom);
  container.bindValue(API_ATOM, api);
  container.bindValue(
    MARKET_PROGRAM_ADDRESS_ATOM,
    marketProgramAddressAtom
  );
  container.bindValue(
    MARKET_IDENTIFIER_ATOM,
    marketIdentifierAtom
  );
  container.bindValue(RPC_URL_ATOM, rpcUrlAtom);
  container.bindValue(PRIMARY_CURRENCY_ATOM, primaryCurrencyAtom);
  return container;
}

// src/client/solana/solana-signer.ts
function createSolanaSigner(input) {
  var _a, _b;
  const defaultSignAll = (transactions) => __async(this, null, function* () {
    return Promise.all(transactions.map((tx) => input.signTransaction(tx)));
  });
  const defaultSignMessage = () => __async(this, null, function* () {
    throw new Error("Solana signMessage is not configured for this signer");
  });
  return {
    publicKey: input.publicKey,
    signTransaction: input.signTransaction,
    signAllTransactions: (_a = input.signAllTransactions) != null ? _a : defaultSignAll,
    signMessage: (_b = input.signMessage) != null ? _b : defaultSignMessage
  };
}

// src/client/solana/program/create-marketplace-program.ts
import { AnchorProvider, Program } from "@coral-xyz/anchor";
import {
  PublicKey
} from "@solana/web3.js";

// src/client/solana/idl/marketplace.json
var marketplace_default = {
  address: "Cni3JRmUwkugWpqpdR7GRRPvBFxoVd6sd43KKZaF9hax",
  metadata: {
    name: "marketplace",
    version: "0.2.8",
    spec: "0.1.0",
    description: "Base marketplace contracts on SVM for rarible"
  },
  instructions: [
    {
      name: "bid",
      docs: ["initializer a new bid"],
      discriminator: [199, 56, 85, 38, 146, 243, 37, 158],
      accounts: [
        {
          name: "initializer",
          writable: true,
          signer: true
        },
        {
          name: "market"
        },
        {
          name: "order",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [111, 114, 100, 101, 114]
              },
              {
                kind: "arg",
                path: "data.nonce"
              },
              {
                kind: "account",
                path: "market"
              },
              {
                kind: "account",
                path: "initializer"
              }
            ]
          }
        },
        {
          name: "initializer_payment_ta",
          writable: true
        },
        {
          name: "order_payment_ta",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "account",
                path: "order"
              },
              {
                kind: "account",
                path: "payment_token_program"
              },
              {
                kind: "account",
                path: "payment_mint"
              }
            ],
            program: {
              kind: "const",
              value: [
                140,
                151,
                37,
                143,
                78,
                36,
                137,
                241,
                187,
                61,
                16,
                41,
                20,
                142,
                13,
                131,
                11,
                90,
                19,
                153,
                218,
                255,
                16,
                132,
                4,
                142,
                123,
                216,
                219,
                233,
                248,
                89
              ]
            }
          }
        },
        {
          name: "payment_mint",
          writable: true
        },
        {
          name: "payment_token_program"
        },
        {
          name: "nft_mint"
        },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        },
        {
          name: "associated_token_program",
          address: "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
        },
        {
          name: "event_authority",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  95,
                  95,
                  101,
                  118,
                  101,
                  110,
                  116,
                  95,
                  97,
                  117,
                  116,
                  104,
                  111,
                  114,
                  105,
                  116,
                  121
                ]
              }
            ]
          }
        },
        {
          name: "program"
        }
      ],
      args: [
        {
          name: "data",
          type: {
            defined: {
              name: "BidData"
            }
          }
        }
      ]
    },
    {
      name: "cancel_bid",
      docs: ["cancel a buy order"],
      discriminator: [40, 243, 190, 217, 208, 253, 86, 206],
      accounts: [
        {
          name: "initializer",
          writable: true,
          signer: true
        },
        {
          name: "order",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [111, 114, 100, 101, 114]
              },
              {
                kind: "account",
                path: "order.nonce",
                account: "Order"
              },
              {
                kind: "account",
                path: "order.market",
                account: "Order"
              },
              {
                kind: "account",
                path: "initializer"
              }
            ]
          }
        },
        {
          name: "market",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [109, 97, 114, 107, 101, 116]
              },
              {
                kind: "account",
                path: "market.market_identifier",
                account: "Market"
              }
            ]
          }
        },
        {
          name: "initializer_payment_ta",
          writable: true
        },
        {
          name: "order_payment_ta",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "account",
                path: "order"
              },
              {
                kind: "account",
                path: "payment_token_program"
              },
              {
                kind: "account",
                path: "payment_mint"
              }
            ],
            program: {
              kind: "const",
              value: [
                140,
                151,
                37,
                143,
                78,
                36,
                137,
                241,
                187,
                61,
                16,
                41,
                20,
                142,
                13,
                131,
                11,
                90,
                19,
                153,
                218,
                255,
                16,
                132,
                4,
                142,
                123,
                216,
                219,
                233,
                248,
                89
              ]
            }
          }
        },
        {
          name: "payment_mint",
          writable: true
        },
        {
          name: "payment_token_program"
        },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        },
        {
          name: "associated_token_program",
          address: "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
        },
        {
          name: "event_authority",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  95,
                  95,
                  101,
                  118,
                  101,
                  110,
                  116,
                  95,
                  97,
                  117,
                  116,
                  104,
                  111,
                  114,
                  105,
                  116,
                  121
                ]
              }
            ]
          }
        },
        {
          name: "program"
        }
      ],
      args: []
    },
    {
      name: "cancel_listing",
      docs: ["cancel a sell order"],
      discriminator: [41, 183, 50, 232, 230, 233, 157, 70],
      accounts: [
        {
          name: "initializer",
          writable: true,
          signer: true
        },
        {
          name: "order",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [111, 114, 100, 101, 114]
              },
              {
                kind: "account",
                path: "order.nonce",
                account: "Order"
              },
              {
                kind: "account",
                path: "order.market",
                account: "Order"
              },
              {
                kind: "account",
                path: "initializer"
              }
            ]
          }
        },
        {
          name: "market",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [109, 97, 114, 107, 101, 116]
              },
              {
                kind: "account",
                path: "market.market_identifier",
                account: "Market"
              }
            ]
          }
        },
        {
          name: "nft_mint",
          writable: true
        },
        {
          name: "initializer_nft_ta",
          writable: true
        },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        },
        {
          name: "sysvar_instructions",
          address: "Sysvar1nstructions1111111111111111111111111"
        },
        {
          name: "associated_token_program",
          address: "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
        },
        {
          name: "nft_token_program"
        },
        {
          name: "nft_program"
        },
        {
          name: "event_authority",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  95,
                  95,
                  101,
                  118,
                  101,
                  110,
                  116,
                  95,
                  97,
                  117,
                  116,
                  104,
                  111,
                  114,
                  105,
                  116,
                  121
                ]
              }
            ]
          }
        },
        {
          name: "program"
        }
      ],
      args: [
        {
          name: "data",
          type: {
            defined: {
              name: "CancelListData"
            }
          }
        }
      ]
    },
    {
      name: "fill_order_v2",
      docs: ["fill a listing"],
      discriminator: [66, 12, 52, 136, 172, 148, 145, 230],
      accounts: [
        {
          name: "taker",
          writable: true,
          signer: true
        },
        {
          name: "maker",
          writable: true
        },
        {
          name: "market",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [109, 97, 114, 107, 101, 116]
              },
              {
                kind: "account",
                path: "market.market_identifier",
                account: "Market"
              }
            ]
          }
        },
        {
          name: "order",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [111, 114, 100, 101, 114]
              },
              {
                kind: "account",
                path: "order.nonce",
                account: "Order"
              },
              {
                kind: "account",
                path: "order.market",
                account: "Order"
              },
              {
                kind: "account",
                path: "order.owner",
                account: "Order"
              }
            ]
          }
        },
        {
          name: "nft_mint",
          writable: true
        },
        {
          name: "seller_nft_ta",
          writable: true
        },
        {
          name: "buyer_nft_ta",
          writable: true
        },
        {
          name: "fee_recipient",
          writable: true
        },
        {
          name: "fee_recipient_ta",
          writable: true
        },
        {
          name: "fee_recipient_2",
          writable: true
        },
        {
          name: "fee_recipient_2_ta",
          writable: true
        },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        },
        {
          name: "nft_token_program"
        },
        {
          name: "nft_program"
        },
        {
          name: "seller_payment_ta",
          writable: true
        },
        {
          name: "maker_payment_ta",
          writable: true
        },
        {
          name: "buyer_payment_ta",
          writable: true
        },
        {
          name: "payment_mint",
          writable: true
        },
        {
          name: "payment_token_program"
        },
        {
          name: "sysvar_instructions",
          address: "Sysvar1nstructions1111111111111111111111111"
        },
        {
          name: "associated_token_program",
          address: "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
        },
        {
          name: "event_authority",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  95,
                  95,
                  101,
                  118,
                  101,
                  110,
                  116,
                  95,
                  97,
                  117,
                  116,
                  104,
                  111,
                  114,
                  105,
                  116,
                  121
                ]
              }
            ]
          }
        },
        {
          name: "program"
        }
      ],
      args: [
        {
          name: "amount",
          type: "u64"
        },
        {
          name: "cnft_args",
          type: {
            option: {
              defined: {
                name: "CnftArgs"
              }
            }
          }
        },
        {
          name: "trait_proof",
          type: {
            option: {
              vec: {
                array: ["u8", 32]
              }
            }
          }
        },
        {
          name: "max_royalty_bps",
          type: "u16"
        },
        {
          name: "expected_price",
          type: "u64"
        }
      ]
    },
    {
      name: "init_market",
      docs: ["initializer a new market"],
      discriminator: [33, 253, 15, 116, 89, 25, 127, 236],
      accounts: [
        {
          name: "initializer",
          writable: true,
          signer: true
        },
        {
          name: "market_identifier",
          signer: true
        },
        {
          name: "market",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [109, 97, 114, 107, 101, 116]
              },
              {
                kind: "account",
                path: "market_identifier"
              }
            ]
          }
        },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        },
        {
          name: "event_authority",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  95,
                  95,
                  101,
                  118,
                  101,
                  110,
                  116,
                  95,
                  97,
                  117,
                  116,
                  104,
                  111,
                  114,
                  105,
                  116,
                  121
                ]
              }
            ]
          }
        },
        {
          name: "program"
        }
      ],
      args: [
        {
          name: "params",
          type: {
            defined: {
              name: "InitMarketParams"
            }
          }
        }
      ]
    },
    {
      name: "list",
      docs: ["initializer a new listing"],
      discriminator: [54, 174, 193, 67, 17, 41, 132, 38],
      accounts: [
        {
          name: "initializer",
          writable: true,
          signer: true
        },
        {
          name: "market",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [109, 97, 114, 107, 101, 116]
              },
              {
                kind: "account",
                path: "market.market_identifier",
                account: "Market"
              }
            ]
          }
        },
        {
          name: "order",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [111, 114, 100, 101, 114]
              },
              {
                kind: "arg",
                path: "data.nonce"
              },
              {
                kind: "account",
                path: "market"
              },
              {
                kind: "account",
                path: "initializer"
              }
            ]
          }
        },
        {
          name: "nft_mint"
        },
        {
          name: "initializer_nft_ta",
          writable: true
        },
        {
          name: "sysvar_instructions",
          address: "Sysvar1nstructions1111111111111111111111111"
        },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        },
        {
          name: "nft_token_program"
        },
        {
          name: "associated_token_program",
          address: "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
        },
        {
          name: "nft_program"
        },
        {
          name: "event_authority",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  95,
                  95,
                  101,
                  118,
                  101,
                  110,
                  116,
                  95,
                  97,
                  117,
                  116,
                  104,
                  111,
                  114,
                  105,
                  116,
                  121
                ]
              }
            ]
          }
        },
        {
          name: "program"
        }
      ],
      args: [
        {
          name: "data",
          type: {
            defined: {
              name: "ListData"
            }
          }
        }
      ]
    },
    {
      name: "modify_market",
      docs: ["modify existing market"],
      discriminator: [195, 49, 4, 91, 235, 161, 139, 178],
      accounts: [
        {
          name: "initializer",
          writable: true,
          signer: true
        },
        {
          name: "market_identifier"
        },
        {
          name: "market",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [109, 97, 114, 107, 101, 116]
              },
              {
                kind: "account",
                path: "market_identifier"
              }
            ]
          }
        },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        },
        {
          name: "event_authority",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  95,
                  95,
                  101,
                  118,
                  101,
                  110,
                  116,
                  95,
                  97,
                  117,
                  116,
                  104,
                  111,
                  114,
                  105,
                  116,
                  121
                ]
              }
            ]
          }
        },
        {
          name: "program"
        }
      ],
      args: [
        {
          name: "params",
          type: {
            defined: {
              name: "ModifyMarketParams"
            }
          }
        }
      ]
    },
    {
      name: "set_core_collection_policy",
      docs: [
        "Grant or revoke a collection's exemption from the #106 MPL Core plugin screen.",
        "Only the market initializer may call it; only the trusted market's policies are ever read."
      ],
      discriminator: [109, 5, 227, 247, 114, 95, 87, 0],
      accounts: [
        {
          name: "initializer",
          writable: true,
          signer: true
        },
        {
          name: "market"
        },
        {
          name: "policy",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [99, 111, 114, 101, 95, 112, 111, 108, 105, 99, 121]
              },
              {
                kind: "arg",
                path: "params.collection"
              },
              {
                kind: "account",
                path: "market"
              }
            ]
          }
        },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        },
        {
          name: "event_authority",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  95,
                  95,
                  101,
                  118,
                  101,
                  110,
                  116,
                  95,
                  97,
                  117,
                  116,
                  104,
                  111,
                  114,
                  105,
                  116,
                  121
                ]
              }
            ]
          }
        },
        {
          name: "program"
        }
      ],
      args: [
        {
          name: "params",
          type: {
            defined: {
              name: "SetCoreCollectionPolicyParams"
            }
          }
        }
      ]
    },
    {
      name: "verify_mint",
      docs: ["initializer a new market"],
      discriminator: [57, 93, 52, 66, 75, 249, 244, 143],
      accounts: [
        {
          name: "initializer",
          writable: true,
          signer: true
        },
        {
          name: "market"
        },
        {
          name: "nft_mint"
        },
        {
          name: "verification",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  118,
                  101,
                  114,
                  105,
                  102,
                  105,
                  99,
                  97,
                  116,
                  105,
                  111,
                  110
                ]
              },
              {
                kind: "account",
                path: "nft_mint"
              },
              {
                kind: "account",
                path: "market"
              }
            ]
          }
        },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        },
        {
          name: "event_authority",
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  95,
                  95,
                  101,
                  118,
                  101,
                  110,
                  116,
                  95,
                  97,
                  117,
                  116,
                  104,
                  111,
                  114,
                  105,
                  116,
                  121
                ]
              }
            ]
          }
        },
        {
          name: "program"
        }
      ],
      args: [
        {
          name: "verified",
          type: "bool"
        }
      ]
    }
  ],
  accounts: [
    {
      name: "CoreCollectionPolicy",
      discriminator: [179, 248, 193, 126, 3, 134, 77, 198]
    },
    {
      name: "Market",
      discriminator: [219, 190, 213, 55, 0, 227, 198, 154]
    },
    {
      name: "MintVerification",
      discriminator: [152, 183, 224, 35, 143, 133, 78, 176]
    },
    {
      name: "Order",
      discriminator: [134, 173, 223, 185, 77, 86, 28, 51]
    }
  ],
  events: [
    {
      name: "CoreCollectionPolicySetEvent",
      discriminator: [199, 61, 110, 130, 92, 235, 130, 58]
    },
    {
      name: "MarketEditEvent",
      discriminator: [160, 230, 206, 202, 154, 28, 249, 246]
    },
    {
      name: "MintVerifiedEvent",
      discriminator: [10, 41, 116, 183, 244, 245, 233, 57]
    },
    {
      name: "OrderEditEvent",
      discriminator: [155, 223, 223, 45, 200, 107, 177, 149]
    }
  ],
  errors: [
    {
      code: 6e3,
      name: "WrongAccount",
      msg: "Account passed in incorrectly"
    },
    {
      code: 6001,
      name: "InsufficientOrderSize",
      msg: "Order too small"
    },
    {
      code: 6002,
      name: "AmountOverflow",
      msg: "Amount overflow"
    },
    {
      code: 6003,
      name: "AmountUnderflow",
      msg: "Amount underflow"
    },
    {
      code: 6004,
      name: "UnsupportedNft",
      msg: "Unsupported NFT Type"
    },
    {
      code: 6005,
      name: "InvalidNft",
      msg: "Invalid NFT for Market"
    },
    {
      code: 6006,
      name: "InvalidFeeAccount",
      msg: "Invalid Fee Account"
    },
    {
      code: 6007,
      name: "InvalidRoyaltiesAccount",
      msg: "Invalid Royalties Account"
    },
    {
      code: 6008,
      name: "InvalidRoyaltyPercentage",
      msg: "Invalid Royalties Percentage"
    },
    {
      code: 6009,
      name: "TotalRoyaltyPercentageExceeded",
      msg: "Total Royalty Percentage Exceeded"
    },
    {
      code: 6010,
      name: "NotEnoughRemainingAccounts",
      msg: "Not Enough Remaining Accounts"
    },
    {
      code: 6011,
      name: "OrderMintMissMatchMintOrGroupMint",
      msg: "Order Mint Miss Match Mint Or GroupMint"
    },
    {
      code: 6012,
      name: "MissingCnftArgs",
      msg: "cNFT args required for Bubblegum NFTs"
    },
    {
      code: 6013,
      name: "InvalidCollectionOwner",
      msg: "Invalid MPL Core collection owner"
    },
    {
      code: 6014,
      name: "InvalidCreatorHash",
      msg: "Creator hash mismatch"
    },
    {
      code: 6015,
      name: "InvalidDataHash",
      msg: "Data hash mismatch"
    },
    {
      code: 6016,
      name: "MissingCollectionAccount",
      msg: "Collection account required for collection-member MPL Core asset"
    },
    {
      code: 6017,
      name: "MarketClosed",
      msg: "Market is not active"
    },
    {
      code: 6018,
      name: "InvalidMarket",
      msg: "Invalid market account"
    },
    {
      code: 6019,
      name: "FeeTooHigh",
      msg: "Market fee exceeds maximum"
    },
    {
      code: 6020,
      name: "InvalidMarketState",
      msg: "Invalid market state"
    },
    {
      code: 6021,
      name: "MissingTraitProof",
      msg: "Trait proof required for trait offer"
    },
    {
      code: 6022,
      name: "InvalidTraitProof",
      msg: "Invalid trait merkle proof"
    },
    {
      code: 6023,
      name: "RoyaltyExceedsMax",
      msg: "Royalty exceeds taker-supplied maximum"
    },
    {
      code: 6024,
      name: "RoyaltyFeeExceedsPrice",
      msg: "Royalty + fee exceed sale price"
    },
    {
      code: 6025,
      name: "UnsupportedNftExtension",
      msg: "Token-2022 mint carries a disallowed extension"
    },
    {
      code: 6026,
      name: "NftNotDelivered",
      msg: "NFT not delivered to buyer after transfer"
    },
    {
      code: 6027,
      name: "NonCanonicalTokenAccount",
      msg: "Token account is not the canonical associated token account for this mint"
    },
    {
      code: 6028,
      name: "NftAlreadyDelegated",
      msg: "NFT token account already has a delegate; cancel the existing listing first"
    },
    {
      code: 6029,
      name: "InvalidOrderSide",
      msg: "Cancel instruction does not match order side"
    },
    {
      code: 6030,
      name: "WnsListingsUnsupported",
      msg: "WNS listings are unsupported; WNS trades via collection bids only"
    },
    {
      code: 6031,
      name: "InvalidAccountVersion",
      msg: "Invalid account version"
    },
    {
      code: 6032,
      name: "UnsupportedCorePlugin",
      msg: "MPL Core asset or collection carries a disallowed plugin"
    },
    {
      code: 6033,
      name: "PriceMismatch",
      msg: "Order price does not match the taker's expected price"
    },
    {
      code: 6034,
      name: "InvalidNftStandard",
      msg: "Order NFT standard is invalid or does not match the supplied NFT program accounts"
    },
    {
      code: 6035,
      name: "FeeExceedsMax",
      msg: "Market fee exceeds the maker's signed maximum"
    },
    {
      code: 6036,
      name: "InvalidRoyaltyCreatorKey",
      msg: "Invalid royalty creator key"
    },
    {
      code: 6037,
      name: "WnsDistributionAccountTooSmall",
      msg: "WNS distribution account is undersized; prime it to its full creator set (via a sell fill or a wallet-authority update_distribution) before filling this bid"
    },
    {
      code: 6038,
      name: "UnsupportedProgrammableEdition",
      msg: "ProgrammableNonFungibleEdition prints are not supported"
    },
    {
      code: 6039,
      name: "TraitProofTooDeep",
      msg: "Trait merkle proof exceeds the maximum depth"
    },
    {
      code: 6040,
      name: "WnsPermanentDelegateNotAllowed",
      msg: "WNS mint carries a live permanent delegate; it can be clawed back after sale"
    },
    {
      code: 6041,
      name: "CoreAssetPermanentlyFrozen",
      msg: "MPL Core asset or its collection is permanently frozen, or its freeze state is unreadable"
    }
  ],
  types: [
    {
      name: "BidData",
      type: {
        kind: "struct",
        fields: [
          {
            name: "nonce",
            type: "pubkey"
          },
          {
            name: "price",
            type: "u64"
          },
          {
            name: "size",
            type: "u64"
          },
          {
            name: "merkle_root",
            docs: [
              "Merkle root over the eligible mint set for a trait offer; pass [0u8; 32] for an ordinary",
              "item/collection bid. Stored on the order and checked at fill (see fill_v2)."
            ],
            type: {
              array: ["u8", 32]
            }
          },
          {
            name: "nft_standard",
            docs: [
              "NFT standard the bid is for (NftStandard discriminant; 0/unknown rejected). The bidder",
              "must declare it \u2014 collection/trait bids cannot derive it on-chain from nft_mint. Stored on",
              "the order and bound at fill, where the declared branch's membership proofs/CPIs must",
              "succeed (#46)."
            ],
            type: "u8"
          },
          {
            name: "max_fee_bps",
            docs: [
              "#89: the maximum combined market fee (fee_bps + fee_bps_2) the maker signed for. Order",
              "creation rejects if the live market fee exceeds this cap, so a modify_market landing",
              "between sign and execution cannot snapshot a fee the maker never saw."
            ],
            type: "u64"
          }
        ]
      }
    },
    {
      name: "CancelListData",
      type: {
        kind: "struct",
        fields: [
          {
            name: "cnft_args",
            type: {
              option: {
                defined: {
                  name: "CnftArgs"
                }
              }
            }
          }
        ]
      }
    },
    {
      name: "CnftArgs",
      docs: [
        "Instruction data for compressed NFT operations via Bubblegum.",
        "`nft_mint` in the instruction accounts must be the asset ID",
        "(keccak256(merkle_tree_pubkey || leaf_index as u64 le) as a Pubkey).",
        "Remaining accounts for list/cancel_list:",
        "[0] tree_authority, [1] merkle_tree (mut), [2] log_wrapper,",
        "[3] compression_program, [4..4+proof_len] merkle proof nodes.",
        "Remaining accounts for fill_v2 append after the proof:",
        "[4+proof_len..] (creator_pubkey, creator_token_account) pairs."
      ],
      type: {
        kind: "struct",
        fields: [
          {
            name: "root",
            type: {
              array: ["u8", 32]
            }
          },
          {
            name: "data_hash",
            type: {
              array: ["u8", 32]
            }
          },
          {
            name: "creator_hash",
            type: {
              array: ["u8", 32]
            }
          },
          {
            name: "nonce",
            type: "u64"
          },
          {
            name: "index",
            type: "u32"
          },
          {
            name: "proof_len",
            type: "u8"
          },
          {
            name: "creator_shares",
            docs: [
              "Share percentage (0\u2013100) for each creator pair in remaining accounts; must sum to 100."
            ],
            type: "bytes"
          },
          {
            name: "creator_verified",
            docs: [
              "Verified flag for each creator, matching Bubblegum's leaf creator list order.",
              "Required to reconstruct and verify creator_hash on-chain."
            ],
            type: {
              vec: "bool"
            }
          },
          {
            name: "metadata",
            docs: [
              "For collection buy-order fills: the full Bubblegum MetadataArgs of the leaf being",
              "sold. Used to recompute the leaf's `data_hash` on-chain so the leaf's real collection",
              "and royalty rate can be trusted (the recomputed hash is in turn verified against the",
              "tree by the Bubblegum transfer CPI). Not consulted for item sell orders.",
              "Boxed so `CnftArgs` stays small on the fill handler's (stack-tight) frame."
            ],
            type: {
              option: {
                defined: {
                  name: "CnftMetadataArgs"
                }
              }
            }
          }
        ]
      }
    },
    {
      name: "CnftCollection",
      type: {
        kind: "struct",
        fields: [
          {
            name: "verified",
            type: "bool"
          },
          {
            name: "key",
            type: "pubkey"
          }
        ]
      }
    },
    {
      name: "CnftCreator",
      type: {
        kind: "struct",
        fields: [
          {
            name: "address",
            type: "pubkey"
          },
          {
            name: "verified",
            type: "bool"
          },
          {
            name: "share",
            type: "u8"
          }
        ]
      }
    },
    {
      name: "CnftMetadataArgs",
      docs: [
        "Mirror of Bubblegum's `MetadataArgs` borsh layout. Each field's serialized bytes must",
        "match Bubblegum exactly so the recomputed `data_hash` equals the on-chain leaf's.",
        "Fieldless enums (token standard / program version / use method) are represented as the",
        "raw `u8` discriminant, which borsh-serializes identically to the original enums."
      ],
      type: {
        kind: "struct",
        fields: [
          {
            name: "name",
            type: "string"
          },
          {
            name: "symbol",
            type: "string"
          },
          {
            name: "uri",
            type: "string"
          },
          {
            name: "seller_fee_basis_points",
            type: "u16"
          },
          {
            name: "primary_sale_happened",
            type: "bool"
          },
          {
            name: "is_mutable",
            type: "bool"
          },
          {
            name: "edition_nonce",
            type: {
              option: "u8"
            }
          },
          {
            name: "token_standard",
            type: {
              option: "u8"
            }
          },
          {
            name: "collection",
            type: {
              option: {
                defined: {
                  name: "CnftCollection"
                }
              }
            }
          },
          {
            name: "uses",
            type: {
              option: {
                defined: {
                  name: "CnftUses"
                }
              }
            }
          },
          {
            name: "token_program_version",
            type: "u8"
          },
          {
            name: "creators",
            type: {
              vec: {
                defined: {
                  name: "CnftCreator"
                }
              }
            }
          }
        ]
      }
    },
    {
      name: "CnftUses",
      type: {
        kind: "struct",
        fields: [
          {
            name: "use_method",
            type: "u8"
          },
          {
            name: "remaining",
            type: "u64"
          },
          {
            name: "total",
            type: "u64"
          }
        ]
      }
    },
    {
      name: "CoreCollectionPolicy",
      docs: [
        "Per-collection exemption from the #106 MPL Core plugin screen, written only by the trusted",
        "market's initializer (the Squads vault). This is what makes the whitelist a runtime list rather",
        "than a compile-time const: granting a collection is one multisig transaction instead of a full",
        "program upgrade.",
        "",
        "It is a real on-chain trading gate, unlike `MintVerification` \u2014 `screen_core_asset` reads it at",
        "both list and fill. It is only ever consulted for `TRUSTED_MARKET`; a policy created under any",
        "other market is inert, which is what stops permissionless `init_market` from turning this into a",
        "self-serve bypass."
      ],
      type: {
        kind: "struct",
        fields: [
          {
            name: "version",
            type: "u8"
          },
          {
            name: "collection",
            docs: [
              "The MPL Core collection this grant applies to. Stored as well as being a PDA seed so the",
              "reader can re-derive and compare rather than trust the caller's account."
            ],
            type: "pubkey"
          },
          {
            name: "market",
            docs: ["The market this grant belongs to. Same reasoning."],
            type: "pubkey"
          },
          {
            name: "extra_plugins",
            docs: [
              "Bit N set => `PluginType` ordinal N is permitted in addition to ALLOWED_CORE_PLUGINS.",
              "Zero revokes the grant without closing the account."
            ],
            type: "u32"
          },
          {
            name: "reserve",
            type: {
              array: ["u8", 64]
            }
          }
        ]
      }
    },
    {
      name: "CoreCollectionPolicySetEvent",
      type: {
        kind: "struct",
        fields: [
          {
            name: "policy",
            type: "pubkey"
          },
          {
            name: "market",
            type: "pubkey"
          },
          {
            name: "market_identifier",
            type: "pubkey"
          },
          {
            name: "collection",
            type: "pubkey"
          },
          {
            name: "initializer",
            type: "pubkey"
          },
          {
            name: "extra_plugins",
            type: "u32"
          }
        ]
      }
    },
    {
      name: "InitMarketParams",
      type: {
        kind: "struct",
        fields: [
          {
            name: "fee_recipient",
            type: "pubkey"
          },
          {
            name: "fee_bps",
            type: "u64"
          },
          {
            name: "fee_recipient_2",
            type: "pubkey"
          },
          {
            name: "fee_bps_2",
            type: "u64"
          }
        ]
      }
    },
    {
      name: "ListData",
      type: {
        kind: "struct",
        fields: [
          {
            name: "nonce",
            type: "pubkey"
          },
          {
            name: "payment_mint",
            type: "pubkey"
          },
          {
            name: "price",
            type: "u64"
          },
          {
            name: "size",
            type: "u64"
          },
          {
            name: "cnft_args",
            type: {
              option: {
                defined: {
                  name: "CnftArgs"
                }
              }
            }
          },
          {
            name: "max_fee_bps",
            docs: [
              "#89: the maximum combined market fee (fee_bps + fee_bps_2) the maker signed for. Order",
              "creation rejects if the live market fee exceeds this cap, so a modify_market landing",
              "between sign and execution cannot snapshot a fee the maker never saw."
            ],
            type: "u64"
          }
        ]
      }
    },
    {
      name: "Market",
      type: {
        kind: "struct",
        fields: [
          {
            name: "version",
            docs: [
              "market account version, used to conditionally parse accounts if changes are made to the struct"
            ],
            type: "u8"
          },
          {
            name: "market_identifier",
            docs: [
              "identifying of the index to which the NFTs belong to (WNS Collection, Metaplex collection, separate hash)"
            ],
            type: "pubkey"
          },
          {
            name: "initializer",
            docs: [
              "initializer of the market - can edit and close the market, admin key"
            ],
            type: "pubkey"
          },
          {
            name: "state",
            docs: ["state representing the market - open/closed"],
            type: "u8"
          },
          {
            name: "fee_recipient",
            docs: ["address that should receive market fees"],
            type: "pubkey"
          },
          {
            name: "fee_bps",
            docs: ["fee basis points"],
            type: "u64"
          },
          {
            name: "fee_recipient_2",
            docs: ["address that should receive market fees"],
            type: "pubkey"
          },
          {
            name: "fee_bps_2",
            docs: ["fee basis points"],
            type: "u64"
          },
          {
            name: "reserve",
            docs: ["reserved space for future changes"],
            type: {
              array: ["u8", 472]
            }
          }
        ]
      }
    },
    {
      name: "MarketEditEvent",
      type: {
        kind: "struct",
        fields: [
          {
            name: "edit_type",
            type: "u8"
          },
          {
            name: "address",
            type: "pubkey"
          },
          {
            name: "version",
            type: "u8"
          },
          {
            name: "market_identifier",
            type: "pubkey"
          },
          {
            name: "initializer",
            type: "pubkey"
          },
          {
            name: "state",
            type: "u8"
          },
          {
            name: "fee_recipient",
            type: "pubkey"
          },
          {
            name: "fee_bps",
            type: "u64"
          },
          {
            name: "fee_recipient_2",
            type: "pubkey"
          },
          {
            name: "fee_bps_2",
            type: "u64"
          }
        ]
      }
    },
    {
      name: "MintVerification",
      docs: [
        "Off-chain/indexer signal only \u2014 NOT an on-chain trading gate.",
        "Nothing in list/bid/fill reads this; markets are not allowlisted by it."
      ],
      type: {
        kind: "struct",
        fields: [
          {
            name: "verified",
            type: "u8"
          }
        ]
      }
    },
    {
      name: "MintVerifiedEvent",
      type: {
        kind: "struct",
        fields: [
          {
            name: "market",
            type: "pubkey"
          },
          {
            name: "market_identifier",
            type: "pubkey"
          },
          {
            name: "nft_mint",
            type: "pubkey"
          },
          {
            name: "initializer",
            type: "pubkey"
          },
          {
            name: "verified",
            type: "u8"
          }
        ]
      }
    },
    {
      name: "ModifyMarketParams",
      type: {
        kind: "struct",
        fields: [
          {
            name: "fee_recipient",
            type: "pubkey"
          },
          {
            name: "fee_bps",
            type: "u64"
          },
          {
            name: "fee_recipient_2",
            type: "pubkey"
          },
          {
            name: "fee_bps_2",
            type: "u64"
          },
          {
            name: "state",
            type: "u8"
          }
        ]
      }
    },
    {
      name: "Order",
      docs: ["order account - each listing has one order account"],
      type: {
        kind: "struct",
        fields: [
          {
            name: "version",
            docs: ["order account version"],
            type: "u8"
          },
          {
            name: "nonce",
            docs: ["nonce for uniqueness"],
            type: "pubkey"
          },
          {
            name: "market",
            docs: ["market to which the order belongs to, must be init'd"],
            type: "pubkey"
          },
          {
            name: "owner",
            docs: ["owner of the order account"],
            type: "pubkey"
          },
          {
            name: "side",
            docs: ["type of order - buy/sell"],
            type: "u8"
          },
          {
            name: "size",
            docs: ["number of bids order is making", "always for 1 for sell"],
            type: "u64"
          },
          {
            name: "price",
            docs: ["bid amount in lamports"],
            type: "u64"
          },
          {
            name: "state",
            docs: ["order state - ready/partial/closed"],
            type: "u8"
          },
          {
            name: "init_time",
            docs: [
              "order account creation time; informational/indexer metadata only. Orders do not expire",
              "on-chain and remain fillable until filled or explicitly cancelled."
            ],
            type: "i64"
          },
          {
            name: "last_edit_time",
            docs: [
              "last time the order was edited; informational/indexer metadata only, not an expiry or",
              "freshness check."
            ],
            type: "i64"
          },
          {
            name: "nft_mint",
            docs: ["nft mint in case order is a sell order"],
            type: "pubkey"
          },
          {
            name: "payment_mint",
            docs: ["mint for the payment, default pubkey if SOL"],
            type: "pubkey"
          },
          {
            name: "fee_bps",
            docs: [
              "market fee basis points snapshotted at order creation; read at fill instead of the live",
              "market so the initializer cannot raise fees on an already-open order"
            ],
            type: "u64"
          },
          {
            name: "fee_bps_2",
            docs: [
              "second market fee basis points, snapshotted at order creation"
            ],
            type: "u64"
          },
          {
            name: "fee_recipient",
            docs: ["market fee recipient snapshotted at order creation"],
            type: "pubkey"
          },
          {
            name: "fee_recipient_2",
            docs: [
              "second market fee recipient snapshotted at order creation"
            ],
            type: "pubkey"
          },
          {
            name: "merkle_root",
            docs: [
              "merkle root over the eligible mint set for a trait offer (buy order); zero = ordinary",
              "item/collection bid. When non-zero, the fill must prove the delivered mint is in this set",
              "(see fill_v2::verify_trait_membership) instead of any per-standard collection membership."
            ],
            type: {
              array: ["u8", 32]
            }
          },
          {
            name: "reserve_0",
            docs: [
              "reserved space for future changes split up due to serialization constraints",
              "(carved down from 256 to make room for the 80-byte fee snapshot above, then 32 more for",
              "the trait-offer merkle_root)"
            ],
            type: {
              array: ["u8", 144]
            }
          },
          {
            name: "reserve_1",
            docs: ["reserved space for future changes"],
            type: {
              array: ["u8", 128]
            }
          },
          {
            name: "reserve_2",
            docs: ["reserved space for future changes"],
            type: {
              array: ["u8", 64]
            }
          },
          {
            name: "reserve_3",
            docs: ["reserved space for future changes"],
            type: {
              array: ["u8", 30]
            }
          },
          {
            name: "reserve_4",
            docs: ["reserved space for future changes"],
            type: {
              array: ["u8", 30]
            }
          },
          {
            name: "royalty_basis_points",
            docs: [
              "royalty basis points snapshotted at order creation for cNFT (leaf sfbp) and generic",
              "Token-2022 (mint metadata) item orders; 0 otherwise. Read at fill so a post-listing",
              "royalty change cannot alter the payout of an already-open order."
            ],
            type: "u16"
          },
          {
            name: "nft_standard",
            docs: [
              "NFT standard the order was created for (see NftStandard; carved out of the reserve like",
              "royalty_basis_points above). Set trustedly at list time from the executed branch, declared",
              "by the bidder at bid time, and bound at fill/cancel so taker-supplied program accounts",
              "cannot select a different standard's transfer branch (#46)."
            ],
            type: "u8"
          }
        ]
      }
    },
    {
      name: "OrderEditEvent",
      type: {
        kind: "struct",
        fields: [
          {
            name: "edit_type",
            type: "u8"
          },
          {
            name: "address",
            type: "pubkey"
          },
          {
            name: "version",
            type: "u8"
          },
          {
            name: "nonce",
            type: "pubkey"
          },
          {
            name: "market",
            type: "pubkey"
          },
          {
            name: "owner",
            type: "pubkey"
          },
          {
            name: "side",
            type: "u8"
          },
          {
            name: "size",
            type: "u64"
          },
          {
            name: "price",
            type: "u64"
          },
          {
            name: "state",
            type: "u8"
          },
          {
            name: "init_time",
            docs: [
              "Informational/indexer metadata only. The marketplace does not enforce expiry from this",
              "timestamp; an active order remains live until filled or explicitly cancelled."
            ],
            type: "i64"
          },
          {
            name: "last_edit_time",
            docs: [
              "Informational/indexer metadata only. This is not used as a freshness or expiry deadline."
            ],
            type: "i64"
          },
          {
            name: "nft_mint",
            type: "pubkey"
          },
          {
            name: "payment_mint",
            type: "pubkey"
          },
          {
            name: "market_identifier",
            type: "pubkey"
          },
          {
            name: "merkle_root",
            docs: [
              "Trait-offer eligibility root (zero for ordinary orders). Emitted so indexers can tell a",
              "trait offer apart from an item/collection bid \u2014 nft_mint alone is ambiguous (on a trait",
              "offer it carries only the discovery collection address)."
            ],
            type: {
              array: ["u8", 32]
            }
          }
        ]
      }
    },
    {
      name: "SetCoreCollectionPolicyParams",
      type: {
        kind: "struct",
        fields: [
          {
            name: "collection",
            docs: [
              "The MPL Core collection being granted (or revoked, with `extra_plugins == 0`)."
            ],
            type: "pubkey"
          },
          {
            name: "extra_plugins",
            docs: [
              "Bit N set => `PluginType` ordinal N permitted beyond ALLOWED_CORE_PLUGINS."
            ],
            type: "u32"
          }
        ]
      }
    }
  ]
};

// src/client/solana/program/marketplace-idl.ts
var marketplaceIdl = marketplace_default;
function marketplaceIdlForProgram(programAddress) {
  return __spreadProps(__spreadValues({}, marketplaceIdl), {
    address: programAddress
  });
}

// src/client/solana/program/create-marketplace-program.ts
function createMarketplaceProgram(container) {
  var _a;
  const connection = container.resolve(CONNECTION_ATOM)();
  const signer = container.resolve(SIGNER_ATOM)();
  const marketProgramAddress = container.resolve(MARKET_PROGRAM_ADDRESS_ATOM)();
  const wallet = {
    publicKey: (_a = signer.publicKey) != null ? _a : PublicKey.default,
    signTransaction: (transaction) => __async(this, null, function* () {
      return signer.signTransaction(transaction);
    }),
    signAllTransactions: (transactions) => __async(this, null, function* () {
      return signer.signAllTransactions(transactions);
    })
  };
  const provider = new AnchorProvider(connection, wallet, {
    commitment: "confirmed"
  });
  return new Program(marketplaceIdlForProgram(marketProgramAddress), provider);
}
function getMarketplaceProgramId(container) {
  const marketProgramAddress = container.resolve(MARKET_PROGRAM_ADDRESS_ATOM)();
  return new PublicKey(marketProgramAddress);
}
function getMarketplaceConnection(container) {
  return container.resolve(CONNECTION_ATOM)();
}

// src/client/solana/program/marketplace-queries.ts
import { AnchorProvider as AnchorProvider2, Program as Program2 } from "@coral-xyz/anchor";
import { PublicKey as PublicKey2 } from "@solana/web3.js";
var MARKETPLACE_ORDER_SIDE_BUY = 0;
var MARKETPLACE_ORDER_SIDE_SELL = 1;
var MARKETPLACE_ORDER_STATE_CLOSED = 2;
var ORDER_ACCOUNT_MARKET_MEMCMP_OFFSET = 8 + 1 + 32;
function deriveMarketPda(programId, marketIdentifier) {
  return PublicKey2.findProgramAddressSync(
    [Buffer.from("market"), marketIdentifier.toBuffer()],
    programId
  )[0];
}
function createReadonlyMarketplaceProgram(connection, programAddress) {
  const provider = new AnchorProvider2(
    connection,
    { publicKey: PublicKey2.default },
    { commitment: "confirmed" }
  );
  return new Program2(marketplaceIdlForProgram(programAddress), provider);
}
function formatMarketplaceOrderState(state) {
  if (state === 0) return "Ready";
  if (state === 1) return "Partial";
  if (state === 2) return "Closed";
  return `Unknown (${state})`;
}
var WRAPPED_SOL_MINT = "So11111111111111111111111111111111111111112";
function formatMarketplacePaymentMint(paymentMint) {
  if (paymentMint === PublicKey2.default.toBase58() || paymentMint === WRAPPED_SOL_MINT) {
    return "SOL";
  }
  return paymentMint;
}
function isActiveMarketplaceSellOrder(order) {
  return order.side === MARKETPLACE_ORDER_SIDE_SELL && order.state !== MARKETPLACE_ORDER_STATE_CLOSED;
}
function mapOrderAccount(params) {
  const paymentMint = params.account.paymentMint.toBase58();
  return {
    orderAddress: params.orderAddress.toBase58(),
    market: params.account.market.toBase58(),
    sellerAddress: params.account.owner.toBase58(),
    nftMint: params.account.nftMint.toBase58(),
    priceLamports: params.account.price.toString(),
    paymentMint,
    paymentLabel: formatMarketplacePaymentMint(paymentMint),
    nonce: params.account.nonce.toBase58(),
    side: params.account.side,
    state: params.account.state,
    stateLabel: formatMarketplaceOrderState(params.account.state)
  };
}
function fetchMarketplaceOrder(params) {
  return __async(this, null, function* () {
    const program = createReadonlyMarketplaceProgram(
      params.connection,
      params.programId
    );
    const orderAddress = new PublicKey2(params.orderAddress);
    const info = yield params.connection.getAccountInfo(orderAddress);
    if (!info || !info.owner.equals(program.programId)) return void 0;
    const account = program.coder.accounts.decode(
      "order",
      info.data
    );
    return mapOrderAccount({ orderAddress, account });
  });
}
function fetchActiveMarketSellOrders(params) {
  return __async(this, null, function* () {
    const programId = new PublicKey2(params.programId);
    const marketIdentifier = new PublicKey2(params.marketIdentifier);
    const marketPda = deriveMarketPda(programId, marketIdentifier);
    const program = createReadonlyMarketplaceProgram(
      params.connection,
      params.programId
    );
    const orderAccounts = program.account;
    const accounts = yield orderAccounts.order.all([
      {
        memcmp: {
          offset: ORDER_ACCOUNT_MARKET_MEMCMP_OFFSET,
          bytes: marketPda.toBase58()
        }
      }
    ]);
    return accounts.map(
      (entry) => mapOrderAccount({
        orderAddress: entry.publicKey,
        account: entry.account
      })
    ).filter(isActiveMarketplaceSellOrder).sort(
      (left, right) => right.priceLamports.localeCompare(left.priceLamports)
    );
  });
}

// src/client/solana/utils/item-id.ts
function toUnionItemId({
  blockchain,
  collectionId,
  tokenId
}) {
  const address = tokenId ? `${collectionId}:${tokenId}` : collectionId;
  return toUnion(blockchain, address);
}

// src/client/solana/order-actions/fill-order-from-chain.ts
import * as anchor from "@coral-xyz/anchor";
import {
  ComputeBudgetProgram as ComputeBudgetProgram2,
  PublicKey as PublicKey10,
  SYSVAR_INSTRUCTIONS_PUBKEY as SYSVAR_INSTRUCTIONS_PUBKEY2,
  SystemProgram as SystemProgram5,
  Transaction as Transaction3,
  TransactionInstruction as TransactionInstruction2
} from "@solana/web3.js";

// src/client/solana/order-actions/cancel-order-from-chain.ts
import {
  ComputeBudgetProgram,
  PublicKey as PublicKey7,
  SYSVAR_INSTRUCTIONS_PUBKEY,
  SystemProgram as SystemProgram3,
  Transaction as Transaction2
} from "@solana/web3.js";

// src/client/solana/order-actions/list-remaining-accounts.ts
import {
  fetchAsset,
  fetchCollection,
  mplCore
} from "@metaplex-foundation/mpl-core";
import { publicKey as umiPublicKey } from "@metaplex-foundation/umi";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import { PublicKey as PublicKey3, SystemProgram } from "@solana/web3.js";

// src/client/solana/order-actions/cnft-das.ts
function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function readErrorMessage(payload) {
  const error = payload.error;
  if (!isPlainObject(error)) return void 0;
  const message = error.message;
  return typeof message === "string" ? message : void 0;
}
function postDasRpc(rpcUrl, method, params) {
  return __async(this, null, function* () {
    const response = yield fetch(rpcUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: "anaconda-sdk-cnft",
        method,
        params
      })
    });
    const payload = yield response.json();
    if (!isPlainObject(payload)) {
      throw new Error(`Solana DAS ${method} returned a non-object payload`);
    }
    const errorMessage = readErrorMessage(payload);
    if (!response.ok || errorMessage !== void 0) {
      throw new Error(
        errorMessage != null ? errorMessage : `Solana DAS ${method} failed with HTTP ${response.status}`
      );
    }
    return payload;
  });
}
function requireResultObject(payload, method) {
  const result = payload.result;
  if (!isPlainObject(result)) {
    throw new Error(`Solana DAS ${method} returned no result`);
  }
  return result;
}
function readOptionalString(value) {
  return typeof value === "string" ? value : void 0;
}
function readOptionalNumber(value) {
  return typeof value === "number" ? value : void 0;
}
function readOptionalBoolean(value) {
  return typeof value === "boolean" ? value : void 0;
}
function parseDasCreator(value) {
  if (!isPlainObject(value)) return void 0;
  const address = readOptionalString(value.address);
  const share = readOptionalNumber(value.share);
  const verified = readOptionalBoolean(value.verified);
  if (address === void 0 || share === void 0 || verified === void 0) {
    return void 0;
  }
  return { address, share, verified };
}
function parseDasCompression(value) {
  if (!isPlainObject(value)) return void 0;
  return {
    compressed: readOptionalBoolean(value.compressed),
    data_hash: readOptionalString(value.data_hash),
    creator_hash: readOptionalString(value.creator_hash),
    asset_hash: readOptionalString(value.asset_hash),
    tree: readOptionalString(value.tree),
    leaf_id: readOptionalNumber(value.leaf_id),
    seq: readOptionalNumber(value.seq)
  };
}
function parseDasGrouping(value) {
  if (!isPlainObject(value)) return void 0;
  return {
    group_key: readOptionalString(value.group_key),
    group_value: readOptionalString(value.group_value),
    verified: readOptionalBoolean(value.verified)
  };
}
function parseDasAsset(result) {
  const creatorsRaw = result.creators;
  const creators = Array.isArray(creatorsRaw) ? creatorsRaw.flatMap((entry) => {
    const creator = parseDasCreator(entry);
    return creator ? [creator] : [];
  }) : void 0;
  const groupingRaw = result.grouping;
  const grouping = Array.isArray(groupingRaw) ? groupingRaw.flatMap((entry) => {
    const group = parseDasGrouping(entry);
    return group ? [group] : [];
  }) : void 0;
  const ownershipRaw = result.ownership;
  const ownership = isPlainObject(ownershipRaw) ? { owner: readOptionalString(ownershipRaw.owner) } : void 0;
  const contentRaw = result.content;
  let content;
  if (isPlainObject(contentRaw)) {
    const metadataRaw = contentRaw.metadata;
    content = isPlainObject(metadataRaw) ? {
      metadata: {
        name: readOptionalString(metadataRaw.name),
        symbol: readOptionalString(metadataRaw.symbol),
        uri: readOptionalString(metadataRaw.uri)
      }
    } : void 0;
  }
  const royaltyRaw = result.royalty;
  const royalty = isPlainObject(royaltyRaw) ? {
    basis_points: readOptionalNumber(royaltyRaw.basis_points),
    primary_sale_happened: readOptionalBoolean(
      royaltyRaw.primary_sale_happened
    )
  } : void 0;
  return {
    id: readOptionalString(result.id),
    compression: parseDasCompression(result.compression),
    ownership,
    creators,
    content,
    royalty,
    mutable: readOptionalBoolean(result.mutable),
    grouping
  };
}
function parseDasAssetProof(result) {
  const root = readOptionalString(result.root);
  const treeId = readOptionalString(result.tree_id);
  const proofRaw = result.proof;
  if (root === void 0 || treeId === void 0 || !Array.isArray(proofRaw)) {
    throw new Error("Solana DAS getAssetProof returned an incomplete result");
  }
  const proof = proofRaw.flatMap(
    (entry) => typeof entry === "string" ? [entry] : []
  );
  if (proof.length !== proofRaw.length) {
    throw new Error("Solana DAS getAssetProof returned non-string proof nodes");
  }
  return {
    root,
    proof,
    tree_id: treeId,
    node_index: readOptionalNumber(result.node_index)
  };
}
function fetchDasAsset(rpcUrl, assetId) {
  return __async(this, null, function* () {
    const payload = yield postDasRpc(rpcUrl, "getAsset", { id: assetId });
    return parseDasAsset(requireResultObject(payload, "getAsset"));
  });
}
function fetchDasAssetProof(rpcUrl, assetId) {
  return __async(this, null, function* () {
    const payload = yield postDasRpc(rpcUrl, "getAssetProof", { id: assetId });
    return parseDasAssetProof(requireResultObject(payload, "getAssetProof"));
  });
}

// src/client/solana/order-actions/list-remaining-accounts.ts
var METAPLEX_METADATA_PROGRAM_ID = new PublicKey3(
  "metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s"
);
var METAPLEX_AUTH_RULES_PROGRAM_ID = new PublicKey3(
  "auth9SigNpDKz4sJJ1DfCTuZrZNSAgh9sFD3rboVmgg"
);
var MPL_CORE_PROGRAM_ID = new PublicKey3(
  "CoREENxT6tW1HoK8ypY1SxRMZTcVPm7R94rH4PZNhX7d"
);
var METADATA_SEED = "metadata";
var EDITION_SEED = "edition";
var TOKEN_RECORD_SEED = "token_record";
var CORE_POLICY_SEED = "core_policy";
var MPL_CORE_ASSET_V1_KEY = 1;
var MPL_CORE_UPDATE_AUTHORITY_OFFSET = 33;
var MPL_CORE_UPDATE_AUTHORITY_COLLECTION = 2;
var MPL_CORE_COLLECTION_ADDRESS_OFFSET = 34;
var PUBLIC_KEY_BYTES = 32;
function getMetaplexMetadataPda(mint) {
  return PublicKey3.findProgramAddressSync(
    [
      Buffer.from(METADATA_SEED),
      METAPLEX_METADATA_PROGRAM_ID.toBuffer(),
      mint.toBuffer()
    ],
    METAPLEX_METADATA_PROGRAM_ID
  )[0];
}
function getMetaplexMasterEditionPda(mint) {
  return PublicKey3.findProgramAddressSync(
    [
      Buffer.from(METADATA_SEED),
      METAPLEX_METADATA_PROGRAM_ID.toBuffer(),
      mint.toBuffer(),
      Buffer.from(EDITION_SEED)
    ],
    METAPLEX_METADATA_PROGRAM_ID
  )[0];
}
function getMetaplexTokenRecordPda(mint, tokenAccount) {
  return PublicKey3.findProgramAddressSync(
    [
      Buffer.from(METADATA_SEED),
      METAPLEX_METADATA_PROGRAM_ID.toBuffer(),
      mint.toBuffer(),
      Buffer.from(TOKEN_RECORD_SEED),
      tokenAccount.toBuffer()
    ],
    METAPLEX_METADATA_PROGRAM_ID
  )[0];
}
function getCoreCollectionPolicyPda(params) {
  return PublicKey3.findProgramAddressSync(
    [
      Buffer.from(CORE_POLICY_SEED),
      params.collection.toBuffer(),
      params.market.toBuffer()
    ],
    params.programId
  )[0];
}
function resolveMetaplexNftProgram(connection, mint) {
  return __async(this, null, function* () {
    const metadataPda = getMetaplexMetadataPda(mint);
    const metadataAccount = yield connection.getAccountInfo(metadataPda);
    if (!metadataAccount) {
      return null;
    }
    if (!metadataAccount.owner.equals(METAPLEX_METADATA_PROGRAM_ID)) {
      return null;
    }
    return METAPLEX_METADATA_PROGRAM_ID;
  });
}
function resolveSolanaNftStandard(params) {
  return __async(this, null, function* () {
    const account = yield params.connection.getAccountInfo(params.address);
    if (!account) {
      return "UNSUPPORTED";
    }
    if (account.owner.equals(MPL_CORE_PROGRAM_ID)) {
      return "METAPLEX_CORE";
    }
    const isSplMint = params.tokenProgramIds.some(
      (tokenProgramId) => account.owner.equals(tokenProgramId)
    );
    if (!isSplMint) {
      return "UNSUPPORTED";
    }
    const nftProgram = yield resolveMetaplexNftProgram(
      params.connection,
      params.address
    );
    if (nftProgram == null ? void 0 : nftProgram.equals(METAPLEX_METADATA_PROGRAM_ID)) {
      return "METAPLEX_TOKEN_METADATA";
    }
    return "UNSUPPORTED";
  });
}
function resolveSolanaNftStandardOrDas(params) {
  return __async(this, null, function* () {
    var _a;
    const onChainStandard = yield resolveSolanaNftStandard(params);
    if (onChainStandard !== "UNSUPPORTED") {
      return onChainStandard;
    }
    try {
      const asset = yield fetchDasAsset(
        params.connection.rpcEndpoint,
        params.address.toBase58()
      );
      if (((_a = asset.compression) == null ? void 0 : _a.compressed) === true) {
        return "BUBBLEGUM";
      }
    } catch (e) {
      return "UNSUPPORTED";
    }
    return "UNSUPPORTED";
  });
}
function parseMplCoreCollectionAddress(data) {
  if (data[0] !== MPL_CORE_ASSET_V1_KEY) {
    return null;
  }
  if (data[MPL_CORE_UPDATE_AUTHORITY_OFFSET] !== MPL_CORE_UPDATE_AUTHORITY_COLLECTION) {
    return null;
  }
  const collectionEndOffset = MPL_CORE_COLLECTION_ADDRESS_OFFSET + PUBLIC_KEY_BYTES;
  if (data.length < collectionEndOffset) {
    return null;
  }
  return new PublicKey3(
    data.subarray(MPL_CORE_COLLECTION_ADDRESS_OFFSET, collectionEndOffset)
  );
}
function buildCoreRemainingAccounts(params) {
  return __async(this, null, function* () {
    const assetAccount = yield params.connection.getAccountInfo(params.asset);
    if (!(assetAccount == null ? void 0 : assetAccount.owner.equals(MPL_CORE_PROGRAM_ID))) {
      return [];
    }
    const collection = parseMplCoreCollectionAddress(assetAccount.data);
    if (!collection) {
      return [];
    }
    return [
      {
        // MPL Core mutates the collection account during asset plugin CPIs,
        // so the transaction must mark it writable or the CPI fails with
        // "writable privilege escalated".
        pubkey: collection,
        isSigner: false,
        isWritable: true
      }
    ];
  });
}
function buildListCoreRemainingAccounts(params) {
  return __async(this, null, function* () {
    var _a;
    const accounts = yield buildCoreRemainingAccounts({
      connection: params.connection,
      asset: params.asset
    });
    const collection = (_a = accounts[0]) == null ? void 0 : _a.pubkey;
    if (!collection) {
      return accounts;
    }
    return [
      ...accounts,
      {
        pubkey: getCoreCollectionPolicyPda({
          collection,
          market: params.market,
          programId: params.programId
        }),
        isSigner: false,
        isWritable: false
      }
    ];
  });
}
function getRoyaltyCreators(obj) {
  var _a, _b;
  return (_b = (_a = obj == null ? void 0 : obj.royalties) == null ? void 0 : _a.creators) != null ? _b : null;
}
var WRAPPED_SOL_MINT2 = new PublicKey3(
  "So11111111111111111111111111111111111111112"
);
var ASSOCIATED_TOKEN_PROGRAM = new PublicKey3(
  "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
);
function royaltyPayoutAccount(creator, payment) {
  if (!payment || payment.mint.equals(WRAPPED_SOL_MINT2)) return creator;
  return PublicKey3.findProgramAddressSync(
    [
      creator.toBuffer(),
      payment.tokenProgram.toBuffer(),
      payment.mint.toBuffer()
    ],
    ASSOCIATED_TOKEN_PROGRAM
  )[0];
}
function buildFillCoreRemainingAccounts(params) {
  return __async(this, null, function* () {
    const assetAccount = yield params.connection.getAccountInfo(params.asset);
    if (!(assetAccount == null ? void 0 : assetAccount.owner.equals(MPL_CORE_PROGRAM_ID))) {
      return { accounts: [], royaltyCreators: [] };
    }
    const umi = createUmi(params.connection.rpcEndpoint).use(mplCore());
    const asset = yield fetchAsset(umi, umiPublicKey(params.asset.toBase58()));
    let collection = null;
    let creators = getRoyaltyCreators(asset);
    const collectionAddress = asset.updateAuthority.address;
    if (asset.updateAuthority.type === "Collection" && collectionAddress) {
      collection = new PublicKey3(collectionAddress.toString());
      const fetchedCollection = yield fetchCollection(umi, collectionAddress);
      creators = creators != null ? creators : getRoyaltyCreators(fetchedCollection);
    }
    const royaltyCreators = (creators != null ? creators : []).map(
      (creator) => new PublicKey3(creator.address.toString())
    );
    return {
      accounts: [
        {
          pubkey: collection != null ? collection : SystemProgram.programId,
          isSigner: false,
          isWritable: collection !== null
        },
        {
          pubkey: collection === null ? SystemProgram.programId : getCoreCollectionPolicyPda({
            collection,
            market: params.market,
            programId: params.programId
          }),
          isSigner: false,
          isWritable: false
        },
        ...royaltyCreators.flatMap((creatorKey) => [
          { pubkey: creatorKey, isSigner: false, isWritable: true },
          {
            pubkey: royaltyPayoutAccount(creatorKey, params.payment),
            isSigner: false,
            isWritable: true
          }
        ])
      ],
      royaltyCreators
    };
  });
}
function parseMetaplexAuthorizationRules(data) {
  const buf = Buffer.isBuffer(data) ? data : Buffer.from(data);
  let offset = 1 + 32 + 32;
  if (offset + 4 > buf.length) return null;
  const nameLength = buf.readUInt32LE(offset);
  offset += 4 + nameLength;
  if (offset + 4 > buf.length) return null;
  const symbolLength = buf.readUInt32LE(offset);
  offset += 4 + symbolLength;
  if (offset + 4 > buf.length) return null;
  const uriLength = buf.readUInt32LE(offset);
  offset += 4 + uriLength;
  if (offset + 2 > buf.length) return null;
  offset += 2;
  if (offset + 1 > buf.length) return null;
  const hasCreators = buf.readUInt8(offset) === 1;
  offset += 1;
  if (hasCreators) {
    if (offset + 4 > buf.length) return null;
    const creatorCount = buf.readUInt32LE(offset);
    offset += 4 + creatorCount * 34;
  }
  if (offset + 2 > buf.length) return null;
  offset += 2;
  if (offset + 1 > buf.length) return null;
  if (buf.readUInt8(offset) === 1) {
    offset += 2;
  } else {
    offset += 1;
  }
  if (offset + 1 > buf.length) return null;
  if (buf.readUInt8(offset) === 1) {
    offset += 2;
  } else {
    offset += 1;
  }
  if (offset + 1 > buf.length) return null;
  if (buf.readUInt8(offset) === 1) {
    offset += 1 + 1 + 32;
  } else {
    offset += 1;
  }
  if (offset + 1 > buf.length) return null;
  if (buf.readUInt8(offset) === 1) {
    offset += 1 + 1 + 8 + 8;
  } else {
    offset += 1;
  }
  if (offset + 1 > buf.length) return null;
  if (buf.readUInt8(offset) === 1) {
    offset += 1;
    if (offset + 1 + 8 > buf.length) return null;
    offset += 1 + 8;
  } else {
    offset += 1;
  }
  if (offset + 1 > buf.length) return null;
  if (buf.readUInt8(offset) !== 1) return null;
  offset += 1;
  if (offset + 1 > buf.length) return null;
  offset += 1;
  if (offset + 1 > buf.length) return null;
  if (buf.readUInt8(offset) !== 1) return null;
  offset += 1;
  if (offset + 32 > buf.length) return null;
  return new PublicKey3(buf.subarray(offset, offset + 32));
}
function buildListRemainingAccounts(params) {
  return __async(this, null, function* () {
    const { connection, mint, ownerTokenAccount } = params;
    const metadataPda = getMetaplexMetadataPda(mint);
    const metadataAccount = yield connection.getAccountInfo(metadataPda);
    if (!metadataAccount) {
      return [];
    }
    const tokenRecordPda = getMetaplexTokenRecordPda(mint, ownerTokenAccount);
    const tokenRecordAccount = yield connection.getAccountInfo(tokenRecordPda);
    const isProgrammableNft = tokenRecordAccount !== null;
    if (isProgrammableNft) {
      const remaining = [
        {
          pubkey: metadataPda,
          isSigner: false,
          isWritable: true
        },
        {
          pubkey: getMetaplexMasterEditionPda(mint),
          isSigner: false,
          isWritable: false
        },
        {
          pubkey: tokenRecordPda,
          isSigner: false,
          isWritable: true
        }
      ];
      const ruleSet = parseMetaplexAuthorizationRules(metadataAccount.data);
      if (ruleSet) {
        remaining.push(
          { pubkey: ruleSet, isSigner: false, isWritable: false },
          {
            pubkey: METAPLEX_AUTH_RULES_PROGRAM_ID,
            isSigner: false,
            isWritable: false
          }
        );
      }
      return remaining;
    }
    return [
      {
        pubkey: metadataPda,
        isSigner: false,
        isWritable: false
      }
    ];
  });
}

// src/client/solana/order-actions/marketplace-program.ts
import { PublicKey as PublicKey4 } from "@solana/web3.js";

// src/exceptions/solana-marketplace-error.ts
var SolanaMarketplaceIdlNotConfiguredError = class extends Error {
  constructor(options) {
    const { marketProgramAddress, instruction, message } = options;
    super(
      message != null ? message : `Solana marketplace IDL is not configured for program ${marketProgramAddress}. Replace packages/anaconda_sdk/src/client/solana/idl/marketplace.json with the Anchor build output before calling ${instruction}.`
    );
    this.code = "SOLANA_MARKETPLACE_IDL_NOT_CONFIGURED";
    this.name = "SolanaMarketplaceIdlNotConfiguredError";
    this.marketProgramAddress = marketProgramAddress;
    this.instruction = instruction;
  }
};
var SolanaMarketplaceNotImplementedError = class extends Error {
  constructor(options) {
    const { action, marketProgramAddress, message } = options;
    super(
      message != null ? message : `Solana marketplace ${action} is not implemented for program ${marketProgramAddress}. Wire the custom program client in anaconda_sdk/src/client/solana/order-actions/.`
    );
    this.code = "SOLANA_MARKETPLACE_NOT_IMPLEMENTED";
    this.name = "SolanaMarketplaceNotImplementedError";
    this.action = action;
    this.marketProgramAddress = marketProgramAddress;
  }
};

// src/client/solana/order-actions/marketplace-program.ts
function assertSolanaSignerReady(container) {
  const signer = container.resolve(SIGNER_ATOM)();
  const publicKey = signer.publicKey;
  if (!publicKey) {
    throw new Error("Solana signer public key is not available");
  }
  return publicKey;
}
function parseSolanaItemId(itemId) {
  const parsed = parseUnionAddress(itemId);
  return {
    blockchain: parsed.blockchain,
    mintAddress: parsed.address,
    tokenId: parsed.tokenId
  };
}

// src/client/solana/order-actions/nft-account-layout.ts
import { PublicKey as PublicKey6, SystemProgram as SystemProgram2 } from "@solana/web3.js";

// src/client/solana/order-actions/cnft-list-accounts.ts
import { PublicKey as PublicKey5 } from "@solana/web3.js";
var BUBBLEGUM_PROGRAM_ID = new PublicKey5(
  "BGUMAp9Gq7iTEuizy4pqaxsTyUCBK68MDfK752saRPUY"
);
var SPL_NOOP_PROGRAM_ID = new PublicKey5(
  "noopb9bkMVfRPU8AsbpTUg8AQkHtKwMYZiFUjNRtMmV"
);
var SPL_ACCOUNT_COMPRESSION_PROGRAM_ID = new PublicKey5(
  "cmtDvXumGCrqC1Age74AVPhSRVXJMd8PJS91L8KbNCK"
);
var LEGACY_TRANSACTION_SIZE_LIMIT = 1232;
function getBubblegumTreeAuthority(merkleTree) {
  return PublicKey5.findProgramAddressSync(
    [merkleTree.toBuffer()],
    BUBBLEGUM_PROGRAM_ID
  )[0];
}
function buildCnftListRemainingAccounts(params) {
  return [
    {
      pubkey: getBubblegumTreeAuthority(params.merkleTree),
      isSigner: false,
      isWritable: false
    },
    {
      pubkey: params.merkleTree,
      isSigner: false,
      isWritable: true
    },
    {
      pubkey: SPL_NOOP_PROGRAM_ID,
      isSigner: false,
      isWritable: false
    },
    {
      pubkey: SPL_ACCOUNT_COMPRESSION_PROGRAM_ID,
      isSigner: false,
      isWritable: false
    },
    ...params.proof.map((node) => ({
      pubkey: node,
      isSigner: false,
      isWritable: false
    }))
  ];
}
function assertLegacyTransactionSize(transaction) {
  try {
    const serialized = transaction.serialize({
      requireAllSignatures: false,
      verifySignatures: false
    });
    if (serialized.length > LEGACY_TRANSACTION_SIZE_LIMIT) {
      throw new Error(
        `Solana cNFT listing transaction is ${serialized.length} bytes (limit ${LEGACY_TRANSACTION_SIZE_LIMIT}). Merkle proof too large; ALT not supported yet.`
      );
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("ALT not supported yet")) {
      throw error;
    }
    const detail = error instanceof Error ? `: ${error.message}` : "";
    throw new Error(
      `Solana cNFT listing transaction exceeds ${LEGACY_TRANSACTION_SIZE_LIMIT} bytes. Merkle proof too large; ALT not supported yet${detail}`
    );
  }
}

// src/client/solana/order-actions/nft-account-layout.ts
var TOKEN_PROGRAM_ID = new PublicKey6(
  "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA"
);
var ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey6(
  "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
);
var TOKEN_2022_PROGRAM_ID = new PublicKey6(
  "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb"
);
function getAssociatedTokenAddressSync(params) {
  const [ata] = PublicKey6.findProgramAddressSync(
    [
      params.owner.toBuffer(),
      params.tokenProgramId.toBuffer(),
      params.mint.toBuffer()
    ],
    ASSOCIATED_TOKEN_PROGRAM_ID
  );
  return ata;
}
function resolveNftAccountLayout(params) {
  if (params.nftStandard === "METAPLEX_CORE") {
    return {
      nftTokenProgram: TOKEN_PROGRAM_ID,
      nftProgram: MPL_CORE_PROGRAM_ID,
      nftTokenAccount: params.nftMint
    };
  }
  if (params.nftStandard === "BUBBLEGUM") {
    return {
      nftTokenProgram: SystemProgram2.programId,
      nftProgram: BUBBLEGUM_PROGRAM_ID,
      nftTokenAccount: getAssociatedTokenAddressSync({
        mint: params.nftMint,
        owner: params.owner,
        tokenProgramId: SystemProgram2.programId
      })
    };
  }
  return {
    nftTokenProgram: params.splTokenProgram,
    nftProgram: METAPLEX_METADATA_PROGRAM_ID,
    nftTokenAccount: getAssociatedTokenAddressSync({
      mint: params.nftMint,
      owner: params.owner,
      tokenProgramId: params.splTokenProgram
    })
  };
}
function resolveMagicEdenTokenAccount(params) {
  return __async(this, null, function* () {
    if (params.nftStandard === "METAPLEX_CORE") {
      return params.nftMint.toBase58();
    }
    const mintAccount = yield params.connection.getAccountInfo(params.nftMint);
    if (!mintAccount) {
      throw new Error(`Mint account not found: ${params.nftMint.toBase58()}`);
    }
    const splTokenProgram = mintAccount.owner.equals(TOKEN_2022_PROGRAM_ID) ? TOKEN_2022_PROGRAM_ID : TOKEN_PROGRAM_ID;
    return resolveNftAccountLayout({
      nftStandard: params.nftStandard,
      nftMint: params.nftMint,
      owner: params.owner,
      splTokenProgram
    }).nftTokenAccount.toBase58();
  });
}

// src/client/solana/order-actions/send-transaction.ts
import {
  PACKET_DATA_SIZE,
  SIGNATURE_LENGTH_IN_BYTES,
  Transaction,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction
} from "@solana/web3.js";
var CONFIRMATION_POLL_INTERVAL_MS = 1e3;
var DEFAULT_COMMITMENT = "confirmed";
var DEFAULT_TRANSACTION_VALIDITY_SLOTS = 150;
var SIGNATURE_COUNT_PREFIX_BYTES = 1;
var MESSAGE_VERSION_FLAG = 128;
function deserializeSolanaTransaction(bytes) {
  return isVersionedTransactionWireFormat(bytes) ? VersionedTransaction.deserialize(bytes) : Transaction.from(bytes);
}
function isVersionedTransactionWireFormat(bytes) {
  const signatureCount = bytes[0];
  if (signatureCount === void 0) return false;
  const messageOffset = SIGNATURE_COUNT_PREFIX_BYTES + signatureCount * SIGNATURE_LENGTH_IN_BYTES;
  const messagePrefix = bytes[messageOffset];
  return messagePrefix !== void 0 && (messagePrefix & MESSAGE_VERSION_FLAG) !== 0;
}
function isLegacySolanaTransaction(transaction) {
  return "instructions" in transaction;
}
function measureLegacyTransactionSize(transaction) {
  const message = transaction.compileMessage();
  return SIGNATURE_COUNT_PREFIX_BYTES + message.header.numRequiredSignatures * SIGNATURE_LENGTH_IN_BYTES + message.serialize().length;
}
function exceedsSolanaPacketSize(transaction) {
  try {
    return measureLegacyTransactionSize(transaction) > PACKET_DATA_SIZE;
  } catch (e) {
    return false;
  }
}
function compileToVersionedTransaction(_0) {
  return __async(this, arguments, function* ({
    connection,
    transaction,
    lookupTableAddresses
  }) {
    const { feePayer, recentBlockhash } = transaction;
    if (!feePayer) {
      throw new Error("Cannot compile a Solana transaction without a fee payer");
    }
    if (!recentBlockhash) {
      throw new Error("Cannot compile a Solana transaction without a blockhash");
    }
    const lookupTables = yield Promise.all(
      lookupTableAddresses.map((address) => __async(this, null, function* () {
        const { value } = yield connection.getAddressLookupTable(address);
        if (!value) {
          throw new Error(
            `Solana address lookup table ${address.toBase58()} was not found`
          );
        }
        return value;
      }))
    );
    const instructions = transaction.instructions.map(
      (instruction) => new TransactionInstruction({
        programId: instruction.programId,
        data: instruction.data,
        keys: instruction.keys.map((key) => __spreadProps(__spreadValues({}, key), {
          isSigner: key.isSigner && key.pubkey.equals(feePayer)
        }))
      })
    );
    const versioned = new VersionedTransaction(
      new TransactionMessage({
        payerKey: feePayer,
        recentBlockhash,
        instructions
      }).compileToV0Message(lookupTables)
    );
    let size;
    try {
      size = versioned.serialize().length;
    } catch (e) {
      throw new Error(
        `Solana transaction still exceeds ${PACKET_DATA_SIZE} bytes after re-encoding against ${lookupTableAddresses.map((address) => address.toBase58()).join(", ")}`
      );
    }
    if (size > PACKET_DATA_SIZE) {
      throw new Error(
        `Solana transaction is ${size} bytes after re-encoding, above the ${PACKET_DATA_SIZE}-byte limit`
      );
    }
    return versioned;
  });
}
function resolveConfirmationExpiry(params) {
  var _a;
  return (_a = params.signedLastValidBlockHeight) != null ? _a : params.currentBlockHeight + DEFAULT_TRANSACTION_VALIDITY_SLOTS;
}
function evaluateSignatureConfirmation(params) {
  const { signatureStatus, currentBlockHeight, lastValidBlockHeight } = params;
  if (signatureStatus == null ? void 0 : signatureStatus.err) {
    return { status: "failed", error: JSON.stringify(signatureStatus.err) };
  }
  if ((signatureStatus == null ? void 0 : signatureStatus.confirmationStatus) === "confirmed" || (signatureStatus == null ? void 0 : signatureStatus.confirmationStatus) === "finalized") {
    return { status: "confirmed" };
  }
  if (currentBlockHeight > lastValidBlockHeight) {
    return { status: "expired" };
  }
  return { status: "pending" };
}
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
function confirmSolanaTransaction(params) {
  return __async(this, null, function* () {
    var _a;
    const { connection, signature, lastValidBlockHeight, commitment } = params;
    while (true) {
      const [statuses, currentBlockHeight] = yield Promise.all([
        connection.getSignatureStatuses([signature]),
        connection.getBlockHeight(commitment)
      ]);
      const outcome = evaluateSignatureConfirmation({
        signatureStatus: (_a = statuses.value[0]) != null ? _a : null,
        currentBlockHeight,
        lastValidBlockHeight
      });
      if (outcome.status === "confirmed") return;
      if (outcome.status === "failed") {
        throw new Error(`Solana transaction failed: ${outcome.error}`);
      }
      if (outcome.status === "expired") {
        const finalStatus = (yield connection.getSignatureStatuses([signature])).value[0];
        if ((finalStatus == null ? void 0 : finalStatus.confirmationStatus) === "confirmed" || (finalStatus == null ? void 0 : finalStatus.confirmationStatus) === "finalized") {
          return;
        }
        if (finalStatus == null ? void 0 : finalStatus.err) {
          throw new Error(
            `Solana transaction failed: ${JSON.stringify(finalStatus.err)}`
          );
        }
        throw new Error(
          `Solana transaction ${signature} was not confirmed before its blockhash expired`
        );
      }
      yield delay(CONFIRMATION_POLL_INTERVAL_MS);
    }
  });
}
function simulateSolanaTransaction(_0) {
  return __async(this, arguments, function* ({
    connection,
    transaction
  }) {
    var _a, _b;
    const versioned = isLegacySolanaTransaction(transaction) ? versionedTransactionForSimulation(transaction) : transaction;
    const simulation = yield connection.simulateTransaction(versioned, {
      sigVerify: false,
      replaceRecentBlockhash: true,
      commitment: DEFAULT_COMMITMENT
    });
    if (simulation.value.err) {
      const logs = (_b = (_a = simulation.value.logs) == null ? void 0 : _a.join("\n")) != null ? _b : "";
      throw new Error(
        `Solana transaction simulation failed: ${JSON.stringify(simulation.value.err)}${logs ? `
${logs}` : ""}`
      );
    }
  });
}
function versionedTransactionForSimulation(transaction) {
  const { feePayer, recentBlockhash, instructions } = transaction;
  if (!feePayer) {
    throw new Error("Cannot simulate a Solana transaction without a fee payer");
  }
  if (!recentBlockhash) {
    throw new Error("Cannot simulate a Solana transaction without a blockhash");
  }
  return new VersionedTransaction(
    new TransactionMessage({
      payerKey: feePayer,
      recentBlockhash,
      instructions
    }).compileToV0Message()
  );
}
function signAndSendSolanaTransaction(_0) {
  return __async(this, arguments, function* ({
    connection,
    signer,
    transaction
  }) {
    const signed = yield signer.signTransaction(transaction);
    const raw = signed.serialize();
    const signature = yield connection.sendRawTransaction(
      Buffer.from(raw.buffer, raw.byteOffset, raw.byteLength),
      { preflightCommitment: DEFAULT_COMMITMENT }
    );
    const lastValidBlockHeight = resolveConfirmationExpiry({
      // Versioned transactions never carry a lastValidBlockHeight.
      signedLastValidBlockHeight: "lastValidBlockHeight" in signed ? signed.lastValidBlockHeight : void 0,
      currentBlockHeight: yield connection.getBlockHeight(DEFAULT_COMMITMENT)
    });
    yield confirmSolanaTransaction({
      connection,
      signature,
      lastValidBlockHeight,
      commitment: DEFAULT_COMMITMENT
    });
    return signature;
  });
}
function fitSolanaTransactionToPacket(_0) {
  return __async(this, arguments, function* ({
    connection,
    transaction,
    lookupTableAddresses
  }) {
    if (!isLegacySolanaTransaction(transaction) || !exceedsSolanaPacketSize(transaction)) {
      return transaction;
    }
    return compileToVersionedTransaction({
      connection,
      transaction,
      lookupTableAddresses
    });
  });
}

// src/client/solana/order-actions/cancel-order-from-chain.ts
var TOKEN_PROGRAM_ID2 = new PublicKey7(
  "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA"
);
var TOKEN_2022_PROGRAM_ID2 = new PublicKey7(
  "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb"
);
var ASSOCIATED_TOKEN_PROGRAM_ID2 = new PublicKey7(
  "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
);
var LEGACY_SOL_PAYMENT_MINT = PublicKey7.default.toBase58();
function isLegacySolPaymentMint(paymentMint) {
  return paymentMint === LEGACY_SOL_PAYMENT_MINT;
}
function resolveTokenProgramForMint(connection, mint) {
  return __async(this, null, function* () {
    const mintAccount = yield connection.getAccountInfo(mint);
    if (!mintAccount) {
      throw new Error(`Mint account not found: ${mint.toBase58()}`);
    }
    return mintAccount.owner.equals(TOKEN_2022_PROGRAM_ID2) ? TOKEN_2022_PROGRAM_ID2 : TOKEN_PROGRAM_ID2;
  });
}
function buildCancelListingInstructionFromChain(params) {
  return __async(this, null, function* () {
    const connection = params.container.resolve(CONNECTION_ATOM)();
    const program = createMarketplaceProgram(params.container);
    const programId = getMarketplaceProgramId(params.container);
    const sellerKey = new PublicKey7(params.seller);
    const orderKey = new PublicKey7(params.orderAddress);
    const orderAccounts = program.account;
    const order = yield orderAccounts.order.fetch(orderKey);
    if (order.side !== MARKETPLACE_ORDER_SIDE_SELL) {
      throw new Error("Only sell listings can be cancelled on this flow");
    }
    if (order.state === MARKETPLACE_ORDER_STATE_CLOSED) {
      throw new Error("Order is already closed");
    }
    if (!order.owner.equals(sellerKey)) {
      throw new Error("Only the listing seller can cancel this order");
    }
    const nftMint = order.nftMint;
    const nftStandard = yield resolveSolanaNftStandard({
      connection,
      address: nftMint,
      tokenProgramIds: [TOKEN_PROGRAM_ID2, TOKEN_2022_PROGRAM_ID2]
    });
    const splTokenProgram = yield resolveTokenProgramForMint(connection, nftMint);
    const {
      nftTokenProgram,
      nftProgram,
      nftTokenAccount: initializerNftTa
    } = resolveNftAccountLayout({
      nftStandard,
      nftMint,
      owner: sellerKey,
      splTokenProgram
    });
    const eventAuthority = PublicKey7.findProgramAddressSync(
      [Buffer.from("__event_authority")],
      programId
    )[0];
    const remainingAccounts = nftStandard === "METAPLEX_CORE" ? yield buildCoreRemainingAccounts({ connection, asset: nftMint }) : yield buildListRemainingAccounts({
      connection,
      mint: nftMint,
      ownerTokenAccount: initializerNftTa
    });
    const instruction = yield program.methods.cancelListing({ cnftArgs: null }).accountsStrict({
      initializer: sellerKey,
      order: orderKey,
      market: order.market,
      nftMint,
      initializerNftTa,
      systemProgram: SystemProgram3.programId,
      sysvarInstructions: SYSVAR_INSTRUCTIONS_PUBKEY,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID2,
      nftTokenProgram,
      nftProgram,
      eventAuthority,
      program: programId
    }).remainingAccounts(remainingAccounts).instruction();
    return { instruction, nftMint };
  });
}
function buildCancelListingTransactionFromChain(params) {
  return __async(this, null, function* () {
    const connection = params.container.resolve(CONNECTION_ATOM)();
    const { instruction, nftMint } = yield buildCancelListingInstructionFromChain(params);
    const transaction = new Transaction2().add(
      ComputeBudgetProgram.setComputeUnitLimit({ units: 85e4 }),
      instruction
    );
    transaction.feePayer = new PublicKey7(params.seller);
    const { blockhash, lastValidBlockHeight } = yield connection.getLatestBlockhash("confirmed");
    transaction.recentBlockhash = blockhash;
    transaction.lastValidBlockHeight = lastValidBlockHeight;
    return { transaction, nftMint: nftMint.toBase58() };
  });
}
function cancelOrderFromChainFactoryReatom(container) {
  return (request) => __async(this, null, function* () {
    var _a;
    if (!container) {
      throw new Error("Solana container is not initialized");
    }
    assertSolanaSignerReady(container);
    const signer = container.resolve(SIGNER_ATOM)();
    if (((_a = signer.publicKey) == null ? void 0 : _a.toBase58()) !== request.from) {
      throw new Error("Solana cancel signer does not match request.from");
    }
    const connection = container.resolve(CONNECTION_ATOM)();
    const { transaction, nftMint } = yield buildCancelListingTransactionFromChain({
      container,
      orderAddress: request.orderAddress,
      seller: request.from
    });
    const txId = yield signAndSendSolanaTransaction({
      connection,
      signer,
      transaction
    });
    return { txId, nftMint };
  });
}

// src/client/solana/order-actions/collection-nft-standard.ts
var NFT_STANDARD_DISCRIMINANTS = {
  MPL_CORE: 1,
  METAPLEX: 2,
  TOKEN2022: 3,
  WNS: 4,
  BUBBLEGUM: 5
};
var ORDER_NFT_STANDARD_LABELS = {
  1: "MPL_CORE",
  2: "METAPLEX",
  3: "TOKEN2022",
  4: "WNS",
  5: "BUBBLEGUM"
};
var SOLANA_COLLECTION_NFT_STANDARDS = new Set(
  Object.keys(NFT_STANDARD_DISCRIMINANTS)
);
function isSolanaCollectionNftStandard(type) {
  return SOLANA_COLLECTION_NFT_STANDARDS.has(type);
}
function parseSolanaCollectionNftStandard(type) {
  if (!type) return void 0;
  if (type === "TOKEN_GROUP_2022") return "TOKEN2022";
  if (isSolanaCollectionNftStandard(type)) return type;
  return void 0;
}
function toBidNftStandardDiscriminant(standard) {
  return NFT_STANDARD_DISCRIMINANTS[standard];
}
function orderNftStandardLabel(discriminant) {
  var _a;
  return (_a = ORDER_NFT_STANDARD_LABELS[discriminant]) != null ? _a : `UNKNOWN(${discriminant})`;
}
function fillAssetStandardToOrderDiscriminant(standard) {
  if (standard === "METAPLEX_CORE") return NFT_STANDARD_DISCRIMINANTS.MPL_CORE;
  if (standard === "METAPLEX_TOKEN_METADATA")
    return NFT_STANDARD_DISCRIMINANTS.METAPLEX;
  if (standard === "BUBBLEGUM") return NFT_STANDARD_DISCRIMINANTS.BUBBLEGUM;
  return void 0;
}
function assertOrderNftStandardMatchesFillAsset(params) {
  const expected = fillAssetStandardToOrderDiscriminant(
    params.fillAssetStandard
  );
  if (expected === void 0) {
    throw new Error(
      `Cannot fill: NFT standard is unsupported for marketplace fills (${params.fillAssetStandard}).`
    );
  }
  if (params.orderNftStandard === expected) return;
  const orderLabel = orderNftStandardLabel(params.orderNftStandard);
  const assetLabel = orderNftStandardLabel(expected);
  throw new Error(
    `Cannot accept this offer: on-chain order NFT standard is ${orderLabel}, but the NFT is ${assetLabel}. Cancel the collection offer and recreate it so the bid stores ${assetLabel} (API collection.type is often wrong for Solana).`
  );
}
function toFillAssetStandard(standard) {
  if (standard === "MPL_CORE") return "METAPLEX_CORE";
  if (standard === "BUBBLEGUM") return "BUBBLEGUM";
  return "METAPLEX_TOKEN_METADATA";
}

// src/client/solana/order-actions/core-freeze-delegate.ts
import { fetchAsset as fetchAsset2, mplCore as mplCore2 } from "@metaplex-foundation/mpl-core";
import { publicKey as umiPublicKey2 } from "@metaplex-foundation/umi";
import { createUmi as createUmi2 } from "@metaplex-foundation/umi-bundle-defaults";
import { PublicKey as PublicKey8 } from "@solana/web3.js";
function resolveCoreFreezeDelegateAuthority(params) {
  return __async(this, null, function* () {
    const umi = createUmi2(params.connection.rpcEndpoint).use(mplCore2());
    const asset = yield fetchAsset2(umi, umiPublicKey2(params.asset.toBase58()));
    const freezeDelegate = asset.freezeDelegate;
    if (!(freezeDelegate == null ? void 0 : freezeDelegate.frozen)) {
      return null;
    }
    const { type, address } = freezeDelegate.authority;
    if (type !== "Address" || !address) {
      return null;
    }
    return new PublicKey8(address.toString());
  });
}

// src/client/solana/order-actions/fill-remaining-accounts.ts
import { PublicKey as PublicKey9, SystemProgram as SystemProgram4 } from "@solana/web3.js";
function getAssociatedTokenAddressSync2(params) {
  const ASSOCIATED_TOKEN_PROGRAM_ID7 = new PublicKey9(
    "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
  );
  const [ata] = PublicKey9.findProgramAddressSync(
    [
      params.owner.toBuffer(),
      params.tokenProgramId.toBuffer(),
      params.mint.toBuffer()
    ],
    ASSOCIATED_TOKEN_PROGRAM_ID7
  );
  return ata;
}
function buildFillRemainingAccounts(params) {
  return __async(this, null, function* () {
    const nftProgram = yield resolveMetaplexNftProgram(
      params.connection,
      params.mint
    );
    if (!(nftProgram == null ? void 0 : nftProgram.equals(METAPLEX_METADATA_PROGRAM_ID))) {
      return [];
    }
    const metadataPda = getMetaplexMetadataPda(params.mint);
    const editionPda = getMetaplexMasterEditionPda(params.mint);
    const sellerTokenRecord = getMetaplexTokenRecordPda(
      params.mint,
      params.sellerNftTa
    );
    const buyerTokenRecord = getMetaplexTokenRecordPda(
      params.mint,
      params.buyerNftTa
    );
    const sellerTokenRecordAccount = yield params.connection.getAccountInfo(sellerTokenRecord);
    const isProgrammableNft = sellerTokenRecordAccount !== null;
    const remainingAccounts = [
      {
        pubkey: metadataPda,
        isSigner: false,
        isWritable: isProgrammableNft
      },
      {
        pubkey: editionPda,
        isSigner: false,
        isWritable: false
      }
    ];
    const metadataAccount = yield params.connection.getAccountInfo(metadataPda);
    const metadataData = metadataAccount ? Buffer.from(metadataAccount.data) : null;
    if (isProgrammableNft) {
      const ruleSet = metadataData ? parseMetaplexAuthorizationRules(metadataData) : null;
      remainingAccounts.push(
        {
          pubkey: sellerTokenRecord,
          isSigner: false,
          isWritable: true
        },
        {
          pubkey: buyerTokenRecord,
          isSigner: false,
          isWritable: true
        },
        {
          pubkey: ruleSet != null ? ruleSet : SystemProgram4.programId,
          isSigner: false,
          isWritable: false
        },
        {
          pubkey: ruleSet ? METAPLEX_AUTH_RULES_PROGRAM_ID : SystemProgram4.programId,
          isSigner: false,
          isWritable: false
        }
      );
    }
    if (!metadataData) {
      return remainingAccounts;
    }
    const royalty = parseMetaplexRoyaltyCreators(metadataData);
    if (royalty.sellerFeeBasisPoints === 0 || royalty.creators.length === 0) {
      return remainingAccounts;
    }
    for (const creator of royalty.creators) {
      remainingAccounts.push({
        pubkey: creator,
        isSigner: false,
        isWritable: true
      });
      remainingAccounts.push({
        pubkey: getAssociatedTokenAddressSync2({
          mint: params.paymentMint,
          owner: creator,
          tokenProgramId: params.paymentTokenProgram
        }),
        isSigner: false,
        isWritable: true
      });
    }
    return remainingAccounts;
  });
}
function parseMetaplexRoyaltyCreators(data) {
  let offset = 1 + 32 + 32;
  if (offset + 4 > data.length) {
    return { sellerFeeBasisPoints: 0, creators: [] };
  }
  const nameLength = data.readUInt32LE(offset);
  offset += 4 + nameLength;
  if (offset + 4 > data.length) {
    return { sellerFeeBasisPoints: 0, creators: [] };
  }
  const symbolLength = data.readUInt32LE(offset);
  offset += 4 + symbolLength;
  if (offset + 4 > data.length) {
    return { sellerFeeBasisPoints: 0, creators: [] };
  }
  const uriLength = data.readUInt32LE(offset);
  offset += 4 + uriLength;
  if (offset + 2 > data.length) {
    return { sellerFeeBasisPoints: 0, creators: [] };
  }
  const sellerFeeBasisPoints = data.readUInt16LE(offset);
  offset += 2;
  if (sellerFeeBasisPoints === 0 || offset + 1 > data.length) {
    return { sellerFeeBasisPoints, creators: [] };
  }
  const hasCreators = data.readUInt8(offset) === 1;
  offset += 1;
  if (!hasCreators || offset + 4 > data.length) {
    return { sellerFeeBasisPoints, creators: [] };
  }
  const creatorCount = data.readUInt32LE(offset);
  offset += 4;
  const creators = [];
  for (let index = 0; index < creatorCount; index += 1) {
    if (offset + 34 > data.length) break;
    const address = new PublicKey9(data.subarray(offset, offset + 32));
    const verified = data.readUInt8(offset + 32) === 1;
    offset += 34;
    if (verified) {
      creators.push(address);
    }
  }
  return { sellerFeeBasisPoints, creators };
}

// src/client/solana/order-actions/fill-order-from-chain.ts
var TOKEN_PROGRAM_ID3 = new PublicKey10(
  "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA"
);
var TOKEN_2022_PROGRAM_ID3 = new PublicKey10(
  "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb"
);
var ASSOCIATED_TOKEN_PROGRAM_ID3 = new PublicKey10(
  "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
);
var WRAPPED_SOL_MINT3 = new PublicKey10(
  "So11111111111111111111111111111111111111112"
);
var CREATE_ASSOCIATED_TOKEN_ACCOUNT_IDEMPOTENT_DISCRIMINATOR = 1;
var TOKEN_SYNC_NATIVE_DISCRIMINATOR = 17;
var ZERO_LAMPORTS = BigInt(0);
var DEFAULT_FILL_MAX_ROYALTY_BPS = 65535;
function resolveFillMaxRoyaltyBps(maxRoyaltyBps) {
  if (maxRoyaltyBps === void 0) return DEFAULT_FILL_MAX_ROYALTY_BPS;
  if (!Number.isInteger(maxRoyaltyBps) || maxRoyaltyBps < 0 || maxRoyaltyBps > 1e4) {
    throw new Error(`Invalid royalty cap ${maxRoyaltyBps} bps`);
  }
  return maxRoyaltyBps;
}
function getAssociatedTokenAddressSync3(params) {
  const [ata] = PublicKey10.findProgramAddressSync(
    [
      params.owner.toBuffer(),
      params.tokenProgramId.toBuffer(),
      params.mint.toBuffer()
    ],
    ASSOCIATED_TOKEN_PROGRAM_ID3
  );
  return ata;
}
function buildCreateAssociatedTokenAccountIdempotentInstruction(params) {
  return new TransactionInstruction2({
    programId: ASSOCIATED_TOKEN_PROGRAM_ID3,
    keys: [
      { pubkey: params.payer, isSigner: true, isWritable: true },
      {
        pubkey: params.associatedTokenAccount,
        isSigner: false,
        isWritable: true
      },
      { pubkey: params.owner, isSigner: false, isWritable: false },
      { pubkey: params.mint, isSigner: false, isWritable: false },
      { pubkey: SystemProgram5.programId, isSigner: false, isWritable: false },
      { pubkey: params.tokenProgramId, isSigner: false, isWritable: false }
    ],
    data: Buffer.from([
      CREATE_ASSOCIATED_TOKEN_ACCOUNT_IDEMPOTENT_DISCRIMINATOR
    ])
  });
}
function buildWrapNativeSolPaymentInstructions(params) {
  if (params.lamports <= ZERO_LAMPORTS) {
    return [];
  }
  return [
    buildCreateAssociatedTokenAccountIdempotentInstruction({
      payer: params.payer,
      owner: params.owner,
      associatedTokenAccount: params.wrappedSolAccount,
      mint: WRAPPED_SOL_MINT3,
      tokenProgramId: TOKEN_PROGRAM_ID3
    }),
    SystemProgram5.transfer({
      fromPubkey: params.payer,
      toPubkey: params.wrappedSolAccount,
      lamports: params.lamports
    }),
    new TransactionInstruction2({
      programId: TOKEN_PROGRAM_ID3,
      keys: [
        { pubkey: params.wrappedSolAccount, isSigner: false, isWritable: true }
      ],
      data: Buffer.from([TOKEN_SYNC_NATIVE_DISCRIMINATOR])
    })
  ];
}
function buildRentExemptionTopUpInstructions(params) {
  return __async(this, null, function* () {
    const uniqueRecipients = Array.from(
      new Map(
        params.recipients.map((recipient) => [recipient.toBase58(), recipient])
      ).values()
    );
    const minRent = yield params.connection.getMinimumBalanceForRentExemption(0);
    const instructions = [];
    for (const recipient of uniqueRecipients) {
      const balance = yield params.connection.getBalance(recipient);
      if (balance < minRent) {
        instructions.push(
          SystemProgram5.transfer({
            fromPubkey: params.payer,
            toPubkey: recipient,
            lamports: minRent - balance
          })
        );
      }
    }
    return instructions;
  });
}
function resolveTokenProgramForMint2(connection, mint) {
  return __async(this, null, function* () {
    const mintAccount = yield connection.getAccountInfo(mint);
    if (!mintAccount) {
      throw new Error(`Mint account not found: ${mint.toBase58()}`);
    }
    return mintAccount.owner.equals(TOKEN_2022_PROGRAM_ID3) ? TOKEN_2022_PROGRAM_ID3 : TOKEN_PROGRAM_ID3;
  });
}
function assertFillableOrder(order) {
  if (order.state === MARKETPLACE_ORDER_STATE_CLOSED) {
    throw new Error("Order is already closed");
  }
  if (isLegacySolPaymentMint(order.paymentMint.toBase58())) {
    throw new Error(
      "This order uses legacy SOL payment encoding and cannot be filled. Cancel and recreate it with wrapped SOL."
    );
  }
}
function resolveFillOrderAccounts(params) {
  if (params.orderSide === MARKETPLACE_ORDER_SIDE_SELL) {
    return {
      mode: "buy_listing",
      seller: params.maker,
      buyer: params.taker,
      nftMint: params.orderNftMint,
      makerPaymentOwner: params.maker,
      buyerPaymentOwner: params.taker
    };
  }
  if (!params.requestedNftMint) {
    throw new Error("Bid-side fill requires requested NFT mint");
  }
  return {
    mode: "accept_bid",
    seller: params.taker,
    buyer: params.maker,
    nftMint: params.requestedNftMint,
    makerPaymentOwner: params.maker,
    buyerPaymentOwner: params.orderAddress
  };
}
function buildFillOrderV2TransactionFromChain(params) {
  return __async(this, null, function* () {
    var _a, _b;
    const amount = (_a = params.amount) != null ? _a : 1;
    if (!Number.isInteger(amount) || amount <= 0) {
      throw new Error("Fill amount must be a positive integer");
    }
    const connection = params.container.resolve(CONNECTION_ATOM)();
    const program = createMarketplaceProgram(params.container);
    const programId = getMarketplaceProgramId(params.container);
    const takerKey = new PublicKey10(params.taker);
    const orderKey = new PublicKey10(params.orderAddress);
    const orderAccounts = program.account;
    const order = yield orderAccounts.order.fetch(orderKey);
    assertFillableOrder(order);
    const makerKey = order.owner;
    const fillAccounts = resolveFillOrderAccounts({
      orderSide: order.side,
      maker: makerKey,
      taker: takerKey,
      orderAddress: orderKey,
      orderNftMint: order.nftMint,
      requestedNftMint: params.nftMint ? new PublicKey10(params.nftMint) : void 0
    });
    const nftMint = fillAccounts.nftMint;
    const paymentMint = order.paymentMint;
    const nftStandard = yield resolveSolanaNftStandard({
      connection,
      address: nftMint,
      tokenProgramIds: [TOKEN_PROGRAM_ID3, TOKEN_2022_PROGRAM_ID3]
    });
    assertOrderNftStandardMatchesFillAsset({
      orderNftStandard: Number(order.nftStandard),
      fillAssetStandard: nftStandard
    });
    const isCoreNft = nftStandard === "METAPLEX_CORE";
    const delistInstructions = [];
    if (isCoreNft && fillAccounts.mode === "accept_bid") {
      const freezeAuthority = yield resolveCoreFreezeDelegateAuthority({
        connection,
        asset: nftMint
      });
      if (freezeAuthority) {
        const listing = yield orderAccounts.order.fetch(freezeAuthority).catch(() => null);
        if (!listing || listing.side !== MARKETPLACE_ORDER_SIDE_SELL || listing.state === MARKETPLACE_ORDER_STATE_CLOSED || !listing.owner.equals(takerKey)) {
          throw new Error(
            "This asset is frozen by a delegate outside this marketplace, so the offer cannot be accepted. Unfreeze it where it was locked, then try again."
          );
        }
        const { instruction: cancelListingInstruction } = yield buildCancelListingInstructionFromChain({
          container: params.container,
          orderAddress: freezeAuthority.toBase58(),
          seller: params.taker
        });
        delistInstructions.push(cancelListingInstruction);
      }
    }
    const splNftTokenProgram = yield resolveTokenProgramForMint2(
      connection,
      nftMint
    );
    const sellerLayout = resolveNftAccountLayout({
      nftStandard,
      nftMint,
      owner: fillAccounts.seller,
      splTokenProgram: splNftTokenProgram
    });
    const buyerLayout = resolveNftAccountLayout({
      nftStandard,
      nftMint,
      owner: fillAccounts.buyer,
      splTokenProgram: splNftTokenProgram
    });
    const nftTokenProgram = sellerLayout.nftTokenProgram;
    const nftProgram = sellerLayout.nftProgram;
    const sellerNftTa = sellerLayout.nftTokenAccount;
    const buyerNftTa = buyerLayout.nftTokenAccount;
    const paymentTokenProgram = paymentMint.equals(WRAPPED_SOL_MINT3) ? TOKEN_PROGRAM_ID3 : yield resolveTokenProgramForMint2(connection, paymentMint);
    const buyerNftTokenAccountSetupInstructions = isCoreNft ? [] : [
      buildCreateAssociatedTokenAccountIdempotentInstruction({
        payer: takerKey,
        owner: fillAccounts.buyer,
        associatedTokenAccount: buyerNftTa,
        mint: nftMint,
        tokenProgramId: nftTokenProgram
      })
    ];
    const sellerPaymentTa = getAssociatedTokenAddressSync3({
      mint: paymentMint,
      owner: fillAccounts.seller,
      tokenProgramId: paymentTokenProgram
    });
    const makerPaymentTa = getAssociatedTokenAddressSync3({
      mint: paymentMint,
      owner: fillAccounts.makerPaymentOwner,
      tokenProgramId: paymentTokenProgram
    });
    const buyerPaymentTa = getAssociatedTokenAddressSync3({
      mint: paymentMint,
      owner: fillAccounts.buyerPaymentOwner,
      tokenProgramId: paymentTokenProgram
    });
    const paymentLamports = BigInt(order.price.toString()) * BigInt(amount);
    const paymentSetupInstructions = fillAccounts.mode === "buy_listing" && paymentMint.equals(WRAPPED_SOL_MINT3) && !params.paidByCard ? buildWrapNativeSolPaymentInstructions({
      payer: takerKey,
      owner: takerKey,
      wrappedSolAccount: buyerPaymentTa,
      lamports: paymentLamports
    }) : [];
    const feeRecipientTa = getAssociatedTokenAddressSync3({
      mint: paymentMint,
      owner: order.feeRecipient,
      tokenProgramId: paymentTokenProgram
    });
    const feeRecipient2Ta = getAssociatedTokenAddressSync3({
      mint: paymentMint,
      owner: order.feeRecipient2,
      tokenProgramId: paymentTokenProgram
    });
    const eventAuthority = PublicKey10.findProgramAddressSync(
      [Buffer.from("__event_authority")],
      programId
    )[0];
    let remainingAccounts;
    let royaltyCreators = [];
    if (isCoreNft) {
      const fillCoreAccounts = yield buildFillCoreRemainingAccounts({
        connection,
        asset: nftMint,
        market: order.market,
        programId,
        payment: { mint: paymentMint, tokenProgram: paymentTokenProgram }
      });
      remainingAccounts = fillCoreAccounts.accounts;
      royaltyCreators = fillCoreAccounts.royaltyCreators;
    } else {
      remainingAccounts = yield buildFillRemainingAccounts({
        connection,
        mint: nftMint,
        paymentMint,
        paymentTokenProgram,
        sellerNftTa,
        buyerNftTa
      });
    }
    const rentExemptionRecipients = fillAccounts.mode === "buy_listing" ? [makerKey, order.feeRecipient, order.feeRecipient2, ...royaltyCreators] : [
      fillAccounts.seller,
      order.feeRecipient,
      order.feeRecipient2,
      ...royaltyCreators
    ];
    const rentExemptionSetupInstructions = paymentMint.equals(WRAPPED_SOL_MINT3) && !params.paidByCard ? yield buildRentExemptionTopUpInstructions({
      connection,
      payer: takerKey,
      recipients: rentExemptionRecipients
    }) : [];
    const instruction = yield program.methods.fillOrderV2(
      new anchor.BN(amount),
      null,
      null,
      resolveFillMaxRoyaltyBps(params.maxRoyaltyBps),
      new anchor.BN(((_b = params.expectedPrice) != null ? _b : order.price).toString())
    ).accountsStrict({
      taker: takerKey,
      maker: makerKey,
      market: order.market,
      order: orderKey,
      nftMint,
      sellerNftTa,
      buyerNftTa,
      feeRecipient: order.feeRecipient,
      feeRecipientTa,
      feeRecipient2: order.feeRecipient2,
      feeRecipient2Ta,
      systemProgram: SystemProgram5.programId,
      nftTokenProgram,
      nftProgram,
      sellerPaymentTa,
      makerPaymentTa,
      buyerPaymentTa,
      paymentMint,
      paymentTokenProgram,
      sysvarInstructions: SYSVAR_INSTRUCTIONS_PUBKEY2,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID3,
      eventAuthority,
      program: programId
    }).remainingAccounts(remainingAccounts).instruction();
    const transaction = new Transaction3().add(
      ComputeBudgetProgram2.setComputeUnitLimit({ units: 85e4 }),
      ...delistInstructions,
      ...rentExemptionSetupInstructions,
      ...buyerNftTokenAccountSetupInstructions,
      ...paymentSetupInstructions,
      instruction
    );
    transaction.feePayer = takerKey;
    const { blockhash, lastValidBlockHeight } = yield connection.getLatestBlockhash("confirmed");
    transaction.recentBlockhash = blockhash;
    transaction.lastValidBlockHeight = lastValidBlockHeight;
    return transaction;
  });
}
function fillOrderFromChainFactoryReatom(container) {
  return (request) => __async(this, null, function* () {
    var _a;
    if (!container) {
      throw new Error("Solana container is not initialized");
    }
    assertSolanaSignerReady(container);
    const signer = container.resolve(SIGNER_ATOM)();
    if (((_a = signer.publicKey) == null ? void 0 : _a.toBase58()) !== request.from) {
      throw new Error("Solana fill signer does not match request.from");
    }
    const connection = container.resolve(CONNECTION_ATOM)();
    const transaction = yield buildFillOrderV2TransactionFromChain({
      container,
      orderAddress: request.orderAddress,
      taker: request.from,
      amount: request.amount,
      nftMint: request.nftMint,
      maxRoyaltyBps: request.maxRoyaltyBps
    });
    const txId = yield signAndSendSolanaTransaction({
      connection,
      signer,
      transaction
    });
    return { txId };
  });
}

// src/client/solana/order-actions/buy.reatom.ts
function resolveOrderAddress(order) {
  return parseUnionAddress(order.id).address;
}
function buyFactoryReatom(container) {
  return (request) => __async(this, null, function* () {
    var _a;
    if (!container) {
      throw new Error("Solana container is not initialized");
    }
    const { blockchain, collectionId, tokenId, order: providedOrder } = request;
    assertSolanaSignerReady(container);
    const from = request.from;
    const signer = container.resolve(SIGNER_ATOM)();
    if (((_a = signer.publicKey) == null ? void 0 : _a.toBase58()) !== from) {
      throw new Error("Solana buy signer does not match request.from");
    }
    let orderAddress;
    if (providedOrder) {
      orderAddress = resolveOrderAddress(providedOrder);
    } else {
      const itemId = toUnionItemId({ blockchain, collectionId, tokenId });
      const { mintAddress } = parseSolanaItemId(itemId);
      const connection = container.resolve(CONNECTION_ATOM)();
      const programId = getMarketplaceProgramId(container);
      const marketIdentifier = container.resolve(MARKET_IDENTIFIER_ATOM)();
      const orders = yield fetchActiveMarketSellOrders({
        connection,
        programId: programId.toBase58(),
        marketIdentifier
      });
      const listing = orders.find((order) => order.nftMint === mintAddress);
      if (!listing) {
        throw new Error("No sell order found for this item");
      }
      orderAddress = listing.orderAddress;
    }
    const fill = fillOrderFromChainFactoryReatom(container);
    const result = yield fill({
      from,
      orderAddress
    });
    return { txId: result.txId };
  });
}

// src/client/solana/order-actions/accept-collection-bid.reatom.ts
function resolveOrderAddress2(order) {
  return parseUnionAddress(order.id).address;
}
function acceptCollectionBidFactoryReatom(container) {
  return (request) => __async(this, null, function* () {
    if (!container) {
      throw new Error("Solana container is not initialized");
    }
    const signerPublicKey = assertSolanaSignerReady(container);
    if (signerPublicKey.toBase58() !== request.from) {
      throw new Error(
        "Solana accept collection bid signer does not match request.from"
      );
    }
    const fill = fillOrderFromChainFactoryReatom(container);
    const successfulTransactions = [];
    const errorTransactions = [];
    for (const itemId of request.itemIds) {
      const order = request.ordersMap[itemId];
      if (!order) {
        throw new Error(`Order not found ${itemId}`);
      }
      try {
        const maxRoyaltyBps = request.maxRoyaltyBpsByItemId[itemId];
        if (maxRoyaltyBps === void 0) {
          throw new Error(`Royalty unknown for ${itemId}`);
        }
        const { mintAddress } = parseSolanaItemId(itemId);
        const result = yield fill({
          from: request.from,
          orderAddress: resolveOrderAddress2(order),
          nftMint: mintAddress,
          maxRoyaltyBps
        });
        successfulTransactions.push({
          itemId,
          order,
          txId: result.txId
        });
      } catch (error) {
        errorTransactions.push({
          itemId,
          order,
          error
        });
      }
    }
    return {
      successfulTransactions,
      errorTransactions
    };
  });
}

// src/client/solana/order-actions/accept-item-bid.reatom.ts
function acceptItemBidFactoryReatom(container) {
  return (request) => __async(this, null, function* () {
    if (!container) {
      throw new Error("Solana container is not initialized");
    }
    const signerPublicKey = assertSolanaSignerReady(container);
    if (signerPublicKey.toBase58() !== request.from) {
      throw new Error(
        "Solana accept item bid signer does not match request.from"
      );
    }
    const { mintAddress } = parseSolanaItemId(request.itemId);
    const fill = fillOrderFromChainFactoryReatom(container);
    const result = yield fill({
      from: request.from,
      orderAddress: parseUnionAddress(request.order.id).address,
      nftMint: mintAddress,
      maxRoyaltyBps: request.maxRoyaltyBps
    });
    return { txId: result.txId };
  });
}

// src/client/solana/order-actions/sell.reatom.ts
import * as anchor3 from "@coral-xyz/anchor";
import {
  ComputeBudgetProgram as ComputeBudgetProgram3,
  Keypair,
  PublicKey as PublicKey13,
  SYSVAR_INSTRUCTIONS_PUBKEY as SYSVAR_INSTRUCTIONS_PUBKEY3,
  SendTransactionError as SendTransactionError2,
  SystemProgram as SystemProgram6,
  Transaction as Transaction4
} from "@solana/web3.js";

// src/client/solana/order-actions/cnft-list-payload.ts
import * as anchor2 from "@coral-xyz/anchor";
import { PublicKey as PublicKey11 } from "@solana/web3.js";
function decodeHash32(label, value) {
  if (!value) {
    throw new Error(`Missing cNFT ${label}`);
  }
  const bytes = Array.from(new PublicKey11(value).toBytes());
  if (bytes.length !== 32) {
    throw new Error(`Invalid cNFT ${label}: expected 32 bytes`);
  }
  return bytes;
}
function requireCreators(creators) {
  if (!(creators == null ? void 0 : creators.length)) {
    throw new Error("cNFT asset is missing creators");
  }
  const shareSum = creators.reduce((sum, creator) => sum + creator.share, 0);
  if (shareSum !== 100) {
    throw new Error(`cNFT creator shares must sum to 100 (got ${shareSum})`);
  }
  return creators;
}
function buildMetadata(asset) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n;
  const name = (_c = (_b = (_a = asset.content) == null ? void 0 : _a.metadata) == null ? void 0 : _b.name) == null ? void 0 : _c.trim();
  const uri = (_f = (_e = (_d = asset.content) == null ? void 0 : _d.metadata) == null ? void 0 : _e.uri) == null ? void 0 : _f.trim();
  if (!name || !uri) {
    throw new Error("cNFT asset is missing name or uri metadata");
  }
  const sellerFeeBasisPoints = (_g = asset.royalty) == null ? void 0 : _g.basis_points;
  if (sellerFeeBasisPoints === void 0) {
    throw new Error("cNFT asset is missing royalty basis points");
  }
  const creators = requireCreators(asset.creators).map((creator) => ({
    address: new PublicKey11(creator.address),
    verified: creator.verified,
    share: creator.share
  }));
  const collectionGrouping = (_h = asset.grouping) == null ? void 0 : _h.find(
    (grouping) => grouping.group_key === "collection" && grouping.group_value
  );
  const collection = (collectionGrouping == null ? void 0 : collectionGrouping.group_value) !== void 0 ? {
    verified: collectionGrouping.verified === true,
    key: new PublicKey11(collectionGrouping.group_value)
  } : null;
  return {
    name,
    symbol: (_k = (_j = (_i = asset.content) == null ? void 0 : _i.metadata) == null ? void 0 : _j.symbol) != null ? _k : "",
    uri,
    sellerFeeBasisPoints,
    primarySaleHappened: (_m = (_l = asset.royalty) == null ? void 0 : _l.primary_sale_happened) != null ? _m : false,
    isMutable: (_n = asset.mutable) != null ? _n : true,
    editionNonce: null,
    tokenStandard: 0,
    collection,
    uses: null,
    tokenProgramVersion: 0,
    creators
  };
}
function buildCnftListArgs(asset, proof) {
  var _a;
  if (((_a = asset.compression) == null ? void 0 : _a.compressed) !== true) {
    throw new Error("Asset is not a compressed NFT");
  }
  const leafId = asset.compression.leaf_id;
  if (leafId === void 0) {
    throw new Error("cNFT asset is missing compression.leaf_id");
  }
  if (proof.proof.length > 255) {
    throw new Error(
      `cNFT merkle proof length ${proof.proof.length} exceeds u8 proofLen`
    );
  }
  const creators = requireCreators(asset.creators);
  return {
    root: decodeHash32("proof root", proof.root),
    dataHash: decodeHash32("data_hash", asset.compression.data_hash),
    creatorHash: decodeHash32("creator_hash", asset.compression.creator_hash),
    nonce: new anchor2.BN(leafId),
    index: leafId,
    proofLen: proof.proof.length,
    creatorShares: Buffer.from(creators.map((creator) => creator.share)),
    creatorVerified: creators.map((creator) => creator.verified),
    metadata: buildMetadata(asset)
  };
}

// src/client/solana/order-actions/sell-asset-mint.ts
import { PublicKey as PublicKey12 } from "@solana/web3.js";
function resolveSellAssetMint(params) {
  var _a;
  const collectionAddress = (_a = params.collectionId.split(":").at(-1)) != null ? _a : params.collectionId;
  const itemId = toUnionItemId({
    blockchain: params.blockchain,
    collectionId: collectionAddress,
    tokenId: params.tokenId
  });
  const { mintAddress, tokenId } = parseSolanaItemId(itemId);
  return new PublicKey12(tokenId != null ? tokenId : mintAddress);
}

// src/client/solana/order-actions/sell.reatom.ts
var TOKEN_PROGRAM_ID4 = new PublicKey13(
  "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA"
);
var TOKEN_2022_PROGRAM_ID4 = new PublicKey13(
  "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb"
);
var ASSOCIATED_TOKEN_PROGRAM_ID4 = new PublicKey13(
  "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
);
var WRAPPED_SOL_MINT4 = new PublicKey13(
  "So11111111111111111111111111111111111111112"
);
var CNFT_LIST_COMPUTE_UNIT_LIMIT = 4e5;
function getAssociatedTokenAddressSync4(params) {
  const [ata] = PublicKey13.findProgramAddressSync(
    [
      params.owner.toBuffer(),
      params.tokenProgramId.toBuffer(),
      params.mint.toBuffer()
    ],
    ASSOCIATED_TOKEN_PROGRAM_ID4
  );
  return ata;
}
function sellFactoryReatom(container) {
  return (request) => __async(this, null, function* () {
    var _a, _b;
    if (!container) {
      throw new Error("Solana container is not initialized");
    }
    assertSolanaSignerReady(container);
    const signer = container.resolve(SIGNER_ATOM)();
    const signerPublicKey = signer.publicKey;
    if (!signerPublicKey) {
      throw new Error("Solana signer public key is not available");
    }
    if (signerPublicKey.toBase58() !== request.from) {
      throw new Error("Solana sell signer does not match request.from");
    }
    const connection = container.resolve(CONNECTION_ATOM)();
    const programId = getMarketplaceProgramId(container);
    const marketIdentifier = new PublicKey13(
      container.resolve(MARKET_IDENTIFIER_ATOM)()
    );
    const nftMint = resolveSellAssetMint({
      blockchain: request.blockchain,
      collectionId: request.collectionId,
      tokenId: request.tokenId
    });
    const paymentMint = new PublicKey13(
      (_a = request.price.currencyId.split(":").at(-1)) != null ? _a : request.price.currencyId
    );
    const nftStandard = yield resolveSolanaNftStandardOrDas({
      connection,
      address: nftMint,
      tokenProgramIds: [TOKEN_PROGRAM_ID4, TOKEN_2022_PROGRAM_ID4]
    });
    if (nftStandard === "UNSUPPORTED") {
      throw new Error("NFT address is not a supported Solana NFT standard.");
    }
    const marketPda = PublicKey13.findProgramAddressSync(
      [Buffer.from("market"), marketIdentifier.toBuffer()],
      programId
    )[0];
    const nonceKeypair = Keypair.generate();
    const nonce = nonceKeypair.publicKey;
    const orderPda = PublicKey13.findProgramAddressSync(
      [
        Buffer.from("order"),
        nonce.toBuffer(),
        marketPda.toBuffer(),
        signerPublicKey.toBuffer()
      ],
      programId
    )[0];
    let cnftArgs = null;
    let nftTokenProgram;
    let nftProgram;
    let initializerNftTa;
    let remainingAccounts;
    if (nftStandard === "BUBBLEGUM") {
      const [dasAsset, dasProof] = yield Promise.all([
        fetchDasAsset(connection.rpcEndpoint, nftMint.toBase58()),
        fetchDasAssetProof(connection.rpcEndpoint, nftMint.toBase58())
      ]);
      if (((_b = dasAsset.ownership) == null ? void 0 : _b.owner) !== signerPublicKey.toBase58()) {
        throw new Error("Connected wallet does not own this compressed NFT");
      }
      cnftArgs = buildCnftListArgs(dasAsset, dasProof);
      const layout = resolveNftAccountLayout({
        nftStandard: "BUBBLEGUM",
        nftMint,
        owner: signerPublicKey,
        splTokenProgram: TOKEN_PROGRAM_ID4
      });
      nftTokenProgram = layout.nftTokenProgram;
      nftProgram = layout.nftProgram;
      initializerNftTa = layout.nftTokenAccount;
      remainingAccounts = buildCnftListRemainingAccounts({
        merkleTree: new PublicKey13(dasProof.tree_id),
        proof: dasProof.proof.map((node) => new PublicKey13(node))
      });
    } else {
      const mintAccount = yield connection.getAccountInfo(nftMint);
      if (!mintAccount) throw new Error("NFT mint account not found");
      nftTokenProgram = nftStandard === "METAPLEX_CORE" ? TOKEN_PROGRAM_ID4 : mintAccount.owner.equals(TOKEN_2022_PROGRAM_ID4) ? TOKEN_2022_PROGRAM_ID4 : TOKEN_PROGRAM_ID4;
      initializerNftTa = nftStandard === "METAPLEX_CORE" ? nftMint : getAssociatedTokenAddressSync4({
        mint: nftMint,
        owner: signerPublicKey,
        tokenProgramId: nftTokenProgram
      });
      nftProgram = nftStandard === "METAPLEX_CORE" ? MPL_CORE_PROGRAM_ID : METAPLEX_METADATA_PROGRAM_ID;
      remainingAccounts = nftStandard === "METAPLEX_CORE" ? yield buildListCoreRemainingAccounts({
        connection,
        asset: nftMint,
        market: marketPda,
        programId
      }) : yield buildListRemainingAccounts({
        connection,
        mint: nftMint,
        ownerTokenAccount: initializerNftTa
      });
      if (nftStandard === "METAPLEX_TOKEN_METADATA" && remainingAccounts.length === 0) {
        throw new Error(
          "NFT mint has no Metaplex Token Metadata account. List supports Metaplex Token Metadata NFTs only."
        );
      }
    }
    const eventAuthority = PublicKey13.findProgramAddressSync(
      [Buffer.from("__event_authority")],
      programId
    )[0];
    const program = createMarketplaceProgram(container);
    const marketAccountInfo = yield connection.getAccountInfo(marketPda);
    if (!marketAccountInfo) throw new Error("Market account not found");
    const market = program.coder.accounts.decode("market", marketAccountInfo.data);
    const maxFeeBps = market.feeBps.add(market.feeBps2);
    const ix = yield program.methods.list({
      nonce,
      paymentMint,
      price: new anchor3.BN(request.price.value),
      size: new anchor3.BN(1),
      cnftArgs,
      maxFeeBps
    }).accounts({
      initializer: signerPublicKey,
      market: marketPda,
      order: orderPda,
      nftMint,
      initializerNftTa,
      sysvarInstructions: SYSVAR_INSTRUCTIONS_PUBKEY3,
      systemProgram: SystemProgram6.programId,
      nftTokenProgram,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID4,
      nftProgram,
      eventAuthority,
      program: programId
    }).remainingAccounts(remainingAccounts).instruction();
    const tx = new Transaction4();
    if (nftStandard === "BUBBLEGUM") {
      tx.add(
        ComputeBudgetProgram3.setComputeUnitLimit({
          units: CNFT_LIST_COMPUTE_UNIT_LIMIT
        })
      );
    }
    tx.add(ix);
    tx.feePayer = signerPublicKey;
    const { blockhash, lastValidBlockHeight } = yield connection.getLatestBlockhash("confirmed");
    tx.recentBlockhash = blockhash;
    tx.lastValidBlockHeight = lastValidBlockHeight;
    if (nftStandard === "BUBBLEGUM") {
      assertLegacyTransactionSize(tx);
    }
    try {
      const txId = yield signAndSendSolanaTransaction({
        connection,
        signer,
        transaction: tx
      });
      return {
        txId,
        orderAddress: orderPda.toBase58(),
        nonce: nonce.toBase58()
      };
    } catch (error) {
      if (error instanceof SendTransactionError2) {
        const logs = yield error.getLogs(connection);
        console.error("simulation logs:", logs);
        console.error(error.message);
      }
      throw error;
    }
  });
}

// src/client/solana/order-actions/cancel-bid-from-chain.ts
import {
  ComputeBudgetProgram as ComputeBudgetProgram4,
  PublicKey as PublicKey14,
  SystemProgram as SystemProgram7,
  Transaction as Transaction5
} from "@solana/web3.js";
var TOKEN_PROGRAM_ID5 = new PublicKey14(
  "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA"
);
var TOKEN_2022_PROGRAM_ID5 = new PublicKey14(
  "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb"
);
var ASSOCIATED_TOKEN_PROGRAM_ID5 = new PublicKey14(
  "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
);
var WRAPPED_SOL_MINT5 = new PublicKey14(
  "So11111111111111111111111111111111111111112"
);
function getAssociatedTokenAddressSync5(params) {
  const [ata] = PublicKey14.findProgramAddressSync(
    [
      params.owner.toBuffer(),
      params.tokenProgramId.toBuffer(),
      params.mint.toBuffer()
    ],
    ASSOCIATED_TOKEN_PROGRAM_ID5
  );
  return ata;
}
function resolveTokenProgramForMint3(connection, mint) {
  return __async(this, null, function* () {
    if (mint.equals(WRAPPED_SOL_MINT5)) {
      return TOKEN_PROGRAM_ID5;
    }
    const mintAccount = yield connection.getAccountInfo(mint);
    if (!mintAccount) {
      throw new Error(`Mint account not found: ${mint.toBase58()}`);
    }
    return mintAccount.owner.equals(TOKEN_2022_PROGRAM_ID5) ? TOKEN_2022_PROGRAM_ID5 : TOKEN_PROGRAM_ID5;
  });
}
function assertCancelableBuyOrder(order, bidder) {
  if (order.side !== MARKETPLACE_ORDER_SIDE_BUY) {
    throw new Error("Only buy orders can be cancelled on this flow");
  }
  if (order.state === MARKETPLACE_ORDER_STATE_CLOSED) {
    throw new Error("Order is already closed");
  }
  if (!order.owner.equals(bidder)) {
    throw new Error("Only the bid owner can cancel this order");
  }
}
function buildCancelBidTransactionFromChain(params) {
  return __async(this, null, function* () {
    const connection = params.container.resolve(CONNECTION_ATOM)();
    const program = createMarketplaceProgram(params.container);
    const programId = getMarketplaceProgramId(params.container);
    const bidderKey = new PublicKey14(params.bidder);
    const orderKey = new PublicKey14(params.orderAddress);
    const orderAccounts = program.account;
    const order = yield orderAccounts.order.fetch(orderKey);
    assertCancelableBuyOrder(order, bidderKey);
    const paymentMint = order.paymentMint;
    const paymentTokenProgram = yield resolveTokenProgramForMint3(
      connection,
      paymentMint
    );
    const initializerPaymentTa = getAssociatedTokenAddressSync5({
      mint: paymentMint,
      owner: bidderKey,
      tokenProgramId: paymentTokenProgram
    });
    const orderPaymentTa = getAssociatedTokenAddressSync5({
      mint: paymentMint,
      owner: orderKey,
      tokenProgramId: paymentTokenProgram
    });
    const eventAuthority = PublicKey14.findProgramAddressSync(
      [Buffer.from("__event_authority")],
      programId
    )[0];
    const instruction = yield program.methods.cancelBid().accountsStrict({
      initializer: bidderKey,
      order: orderKey,
      market: order.market,
      initializerPaymentTa,
      orderPaymentTa,
      paymentMint,
      paymentTokenProgram,
      systemProgram: SystemProgram7.programId,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID5,
      eventAuthority,
      program: programId
    }).instruction();
    const transaction = new Transaction5().add(
      ComputeBudgetProgram4.setComputeUnitLimit({ units: 2e5 }),
      instruction
    );
    transaction.feePayer = bidderKey;
    const { blockhash, lastValidBlockHeight } = yield connection.getLatestBlockhash("confirmed");
    transaction.recentBlockhash = blockhash;
    transaction.lastValidBlockHeight = lastValidBlockHeight;
    return transaction;
  });
}
function cancelBidFromChainFactoryReatom(container) {
  return (request) => __async(this, null, function* () {
    var _a;
    if (!container) {
      throw new Error("Solana container is not initialized");
    }
    assertSolanaSignerReady(container);
    const signer = container.resolve(SIGNER_ATOM)();
    if (((_a = signer.publicKey) == null ? void 0 : _a.toBase58()) !== request.from) {
      throw new Error("Solana cancel bid signer does not match request.from");
    }
    const connection = container.resolve(CONNECTION_ATOM)();
    const transaction = yield buildCancelBidTransactionFromChain({
      container,
      orderAddress: request.orderAddress,
      bidder: request.from
    });
    const txId = yield signAndSendSolanaTransaction({
      connection,
      signer,
      transaction
    });
    return { txId };
  });
}

// src/client/solana/order-actions/cancel.reatom.ts
function cancelFactoryReatom(container) {
  return (request) => __async(this, null, function* () {
    if (!container) {
      throw new Error("Solana container is not initialized");
    }
    const seller = assertSolanaSignerReady(container);
    const sellerAddress = seller.toBase58();
    const connection = container.resolve(CONNECTION_ATOM)();
    const programId = getMarketplaceProgramId(container);
    const marketIdentifier = container.resolve(MARKET_IDENTIFIER_ATOM)();
    const { mintAddress } = parseSolanaItemId(request.itemId);
    const orders = yield fetchActiveMarketSellOrders({
      connection,
      programId: programId.toBase58(),
      marketIdentifier
    });
    const listing = orders.find(
      (order) => order.nftMint === mintAddress && order.sellerAddress === sellerAddress
    );
    if (!listing) {
      throw new Error("Order not found");
    }
    const cancelFromChain = cancelOrderFromChainFactoryReatom(container);
    const result = yield cancelFromChain({
      from: sellerAddress,
      orderAddress: listing.orderAddress
    });
    return result.txId;
  });
}

// src/client/solana/order-actions/cancel-by-order.reatom.ts
function cancelByOrderFactoryReatom(container) {
  return (request) => __async(this, null, function* () {
    if (!container) {
      throw new Error("Solana container is not initialized");
    }
    const bidder = assertSolanaSignerReady(container);
    const orderAddress = parseUnionAddress(request.orderId).address;
    const cancelBid = cancelBidFromChainFactoryReatom(container);
    const result = yield cancelBid({
      from: bidder.toBase58(),
      orderAddress
    });
    return result.txId;
  });
}

// src/client/solana/order-actions/bid-common.ts
import * as anchor4 from "@coral-xyz/anchor";
import {
  Keypair as Keypair2,
  PublicKey as PublicKey15,
  SendTransactionError as SendTransactionError3,
  SystemProgram as SystemProgram8,
  Transaction as Transaction6
} from "@solana/web3.js";
var TOKEN_PROGRAM_ID6 = new PublicKey15(
  "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA"
);
var ASSOCIATED_TOKEN_PROGRAM_ID6 = new PublicKey15(
  "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
);
var WRAPPED_SOL_MINT6 = new PublicKey15(
  "So11111111111111111111111111111111111111112"
);
function getAssociatedTokenAddressSync6(params) {
  const [ata] = PublicKey15.findProgramAddressSync(
    [
      params.owner.toBuffer(),
      params.tokenProgramId.toBuffer(),
      params.mint.toBuffer()
    ],
    ASSOCIATED_TOKEN_PROGRAM_ID6
  );
  return ata;
}
function submitBid(params) {
  return __async(this, null, function* () {
    var _a;
    const { container } = params;
    if (!container) {
      throw new Error("Solana container is not initialized");
    }
    assertSolanaSignerReady(container);
    const signer = container.resolve(SIGNER_ATOM)();
    const signerPublicKey = signer.publicKey;
    if (!signerPublicKey) {
      throw new Error("Solana signer public key is not available");
    }
    if (signerPublicKey.toBase58() !== params.from) {
      throw new Error(params.signerMismatchMessage);
    }
    const connection = container.resolve(CONNECTION_ATOM)();
    const programId = getMarketplaceProgramId(container);
    const marketIdentifier = new PublicKey15(
      container.resolve(MARKET_IDENTIFIER_ATOM)()
    );
    const nftMint = new PublicKey15(params.nftMint);
    const paymentMint = new PublicKey15(
      (_a = params.price.currencyId.split(":").at(-1)) != null ? _a : params.price.currencyId
    );
    const marketPda = PublicKey15.findProgramAddressSync(
      [Buffer.from("market"), marketIdentifier.toBuffer()],
      programId
    )[0];
    const nonceKeypair = Keypair2.generate();
    const nonce = nonceKeypair.publicKey;
    const orderPda = PublicKey15.findProgramAddressSync(
      [
        Buffer.from("order"),
        nonce.toBuffer(),
        marketPda.toBuffer(),
        signerPublicKey.toBuffer()
      ],
      programId
    )[0];
    const program = createMarketplaceProgram(container);
    const marketAccountInfo = yield connection.getAccountInfo(marketPda);
    if (!marketAccountInfo) throw new Error("Market account not found");
    const market = program.coder.accounts.decode("market", marketAccountInfo.data);
    const maxFeeBps = market.feeBps.add(market.feeBps2);
    const initializerPaymentTa = getAssociatedTokenAddressSync6({
      mint: paymentMint,
      owner: signerPublicKey,
      tokenProgramId: TOKEN_PROGRAM_ID6
    });
    const orderPaymentTa = getAssociatedTokenAddressSync6({
      mint: paymentMint,
      owner: orderPda,
      tokenProgramId: TOKEN_PROGRAM_ID6
    });
    const eventAuthority = PublicKey15.findProgramAddressSync(
      [Buffer.from("__event_authority")],
      programId
    )[0];
    const ix = yield program.methods.bid({
      nonce,
      price: new anchor4.BN(params.price.value),
      size: new anchor4.BN(1),
      merkleRoot: Array.from({ length: 32 }, () => 0),
      nftStandard: toBidNftStandardDiscriminant(params.nftStandard),
      maxFeeBps
    }).accounts({
      initializer: signerPublicKey,
      market: marketPda,
      order: orderPda,
      initializerPaymentTa,
      orderPaymentTa,
      paymentMint,
      paymentTokenProgram: TOKEN_PROGRAM_ID6,
      nftMint,
      systemProgram: SystemProgram8.programId,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID6,
      eventAuthority,
      program: programId
    }).instruction();
    const paymentSetupInstructions = paymentMint.equals(WRAPPED_SOL_MINT6) ? buildWrapNativeSolPaymentInstructions({
      payer: signerPublicKey,
      owner: signerPublicKey,
      wrappedSolAccount: initializerPaymentTa,
      lamports: BigInt(params.price.value)
    }) : [];
    const tx = new Transaction6().add(...paymentSetupInstructions, ix);
    tx.feePayer = signerPublicKey;
    const { blockhash, lastValidBlockHeight } = yield connection.getLatestBlockhash("confirmed");
    tx.recentBlockhash = blockhash;
    tx.lastValidBlockHeight = lastValidBlockHeight;
    try {
      const txId = yield signAndSendSolanaTransaction({
        connection,
        signer,
        transaction: tx
      });
      return {
        txId,
        orderAddress: orderPda.toBase58(),
        nonce: nonce.toBase58()
      };
    } catch (error) {
      if (error instanceof SendTransactionError3) {
        const logs = yield error.getLogs(connection);
        console.error("simulation logs:", logs);
        console.error(error.message);
      }
      throw error;
    }
  });
}

// src/client/solana/order-actions/collection-bid.reatom.ts
function collectionOfferFactoryReatom(container) {
  return (request) => __async(this, null, function* () {
    return submitBid({
      container,
      from: request.from,
      // A bid whose nft_mint is the collection address is a collection offer.
      nftMint: request.collectionAddress,
      nftStandard: request.nftStandard,
      price: request.price,
      signerMismatchMessage: "Solana collection offer signer does not match request.from"
    });
  });
}

// src/client/solana/order-actions/item-bid.reatom.ts
var ITEM_BID_SUPPORTED_NFT_STANDARDS = [
  "METAPLEX",
  "MPL_CORE",
  "TOKEN2022"
];
function isItemBidSupportedNftStandard(standard) {
  if (!standard) return false;
  return ITEM_BID_SUPPORTED_NFT_STANDARDS.includes(
    standard
  );
}
function itemOfferFactoryReatom(container) {
  return (request) => __async(this, null, function* () {
    if (!isItemBidSupportedNftStandard(request.nftStandard)) {
      throw new Error(
        `Item offers are not supported for ${request.nftStandard} NFTs. Make a collection offer instead.`
      );
    }
    return submitBid({
      container,
      from: request.from,
      // A bid whose nft_mint is the item's own mint is an item offer.
      nftMint: request.itemMint,
      nftStandard: request.nftStandard,
      price: request.price,
      signerMismatchMessage: "Solana item offer signer does not match request.from"
    });
  });
}
export {
  ITEM_BID_SUPPORTED_NFT_STANDARDS,
  LEGACY_SOL_PAYMENT_MINT,
  MARKETPLACE_ORDER_SIDE_BUY,
  MARKETPLACE_ORDER_SIDE_SELL,
  MARKETPLACE_ORDER_STATE_CLOSED,
  ORDER_ACCOUNT_MARKET_MEMCMP_OFFSET,
  SolanaMarketplaceIdlNotConfiguredError,
  SolanaMarketplaceNotImplementedError,
  solana_container_tokens_reatom_exports as SolanaTokens,
  acceptCollectionBidFactoryReatom,
  acceptItemBidFactoryReatom,
  assertOrderNftStandardMatchesFillAsset,
  buildFillOrderV2TransactionFromChain,
  buyFactoryReatom,
  cancelBidFromChainFactoryReatom,
  cancelByOrderFactoryReatom,
  cancelFactoryReatom,
  cancelOrderFromChainFactoryReatom,
  collectionOfferFactoryReatom,
  compileToVersionedTransaction,
  createMarketplaceProgram,
  createReatomSolanaContainer,
  createSolanaApi,
  createSolanaSigner,
  deriveMarketPda,
  deserializeSolanaTransaction,
  exceedsSolanaPacketSize,
  fetchActiveMarketSellOrders,
  fetchMarketplaceOrder,
  fillOrderFromChainFactoryReatom,
  fitSolanaTransactionToPacket,
  formatMarketplaceOrderState,
  formatMarketplacePaymentMint,
  getMarketplaceConnection,
  getMarketplaceProgramId,
  isActiveMarketplaceSellOrder,
  isItemBidSupportedNftStandard,
  isLegacySolPaymentMint,
  isLegacySolanaTransaction,
  itemOfferFactoryReatom,
  parseSolanaCollectionNftStandard,
  parseUnionAddress,
  resolveMagicEdenTokenAccount,
  sellFactoryReatom,
  signAndSendSolanaTransaction,
  simulateSolanaTransaction,
  toBidNftStandardDiscriminant,
  toFillAssetStandard,
  toUnion,
  toUnionItemId
};
//# sourceMappingURL=index.mjs.map